// CANONICAL — active Offices page component.
// Three views controlled by viewMode state:
//   viewMode === "list"   → sortable table (default)
//   viewMode === "tiles"  → card grid
//   viewMode === "map"    → map view
// Do not edit OfficesPage.jsx or PortfolioView.jsx expecting changes here.
// src/v2/locations/OfficesPage.jsx
//
// LOCATIONS-V2-S1: v2 design-system recomp of the Locations list page.
//
// Features:
//   - 6 hover-lift KPI tiles (stats row)
//   - Consolidated multi-select filter (AND across dimensions, OR within)
//   - Saved view presets (All active, EMEA, AMER, At risk, In-build)
//   - CSS-grid table, 11 columns, no <table> element
//   - Density toggle (comfortable / compact)
//   - Slide-in detail drawer via SlideSheet
//   - Tiles view via LocationsGrid
//   - Map view via LocationsMap

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useLocation as useRouterLocation } from "react-router-dom";
import { buildOfficeFullUrl, buildOfficeDrawerUrl, normalizeOfficeDrawerTab, buildProjectUrl, buildMarketUrl } from "../../services/buildUrl.js";
import { OfficeRecordDrawer, officeIdentityLine } from "../../features/locations/OfficeRecordDrawer.jsx";
import { readRecordReturn, recordSwapState } from "../../services/recordReturn.js";
import { TriangleAlert } from "lucide-react";
import { activateCollectionRow } from "../../utils/collectionRowNavigation.js";
import { T, STAGES, REGIONS } from "../../app/tokens.js";
import { USABLE_CAPACITY_STAGES } from "../../utils/capacityStages.js";
import { S } from "../../app/styles.js";
import { getLocations } from "../../data/locationsClient.js";
import { useRecordPhotoRefresh } from "../../hooks/useRecordPhotoRefresh.js";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KpiPopover, HOME_KPI_TOOLTIP_STYLE } from "../../components/KpiPopover.jsx";
import { KPI_TILE_ROW_HEIGHT } from "../../features/plan/KpiTileRow.jsx";
import { KpiBand } from "../../components/KpiBand.jsx";
import { PageHeaderStack } from "../../components/PageHeaderStack.jsx";
import { getHeadcountTrendReport } from "../../data/reportsClient.js";
import { SortControl } from "../components/SortControl.jsx";
import { getDisplayedSqft, isServicedNoArea } from "../../utils/locationSqftDerived.js";
import { formatReportingMonth, reportingMonthKey } from "../../services/reportingPeriod.js";
import { ServicedNaLabel } from "../../components/ServicedNaLabel.jsx";
import { getActiveLeaseTypes } from "../../utils/leases.js";
import { applySearchFilter, matchOfficeIdentity } from "../../services/filterLocations.js";
import { rankIdentityMatches } from "../../utils/curatedSearch.js";
import { IdentityMatchHint } from "../../components/IdentityMatchHint.jsx";
import { STAGE_ORDER } from "../../utils/rankSites.js";
import { M_AND_A_TAGS, getMAndATagName } from "../../utils/mAndA.js";
import { LEASE_TYPES, LEASE_DISPLAY } from "../../utils/leaseTypeConstants.js";
import { fmtFootprintSqft as fmtSqft, fmtInt } from "../../utils/format.js";
import { StageBadge } from "../../components/StageBadge.jsx";
import { ConsolidatedFilter } from "../components/ConsolidatedFilter.jsx";
import { LIST_PRESENTATION, listHeaderEdges, COLLECTION_GEOMETRY, collectionRowHeight } from "../components/listPresentation.js";
import { SearchInput } from "../components/SearchInput.jsx";
import { FilterHiddenSearchHint } from "../components/FilterHiddenSearchHint.jsx";
import { restrictedFilterGroups, relaxFilterGroups, deriveFilterHiddenSearch } from "../../utils/filterHiddenSearch.js";
import { InlineFilterToolbar, InlineFilterChips, InlineFilterClear, facetChips } from "../components/InlineFilterToolbar.jsx";
import { CHECKED_FILTER_VERSION, allOptionValues, normalizeCheckedSelections, checkedPredicateSelections, withFacetCounts } from "../../utils/checkedSelections.js";
import { ViewControls, CompactSwitch } from "../components/ViewControls.jsx";
import { ToolbarAddButton } from "../components/ToolbarAddButton.jsx";
import { ToolbarViewToggle } from "../components/ToolbarViewToggle.jsx";
import { AlertChip } from "../components/AlertChip.jsx";
import { Tooltip } from "../../components/Tooltip.jsx";
import { useHoverIntent } from "../../components/useHoverIntent.js";
import { log } from "../../utils/logger.js";
import { subscribeAttendanceSourceChange } from "../../services/cacheBus.js";
import { useUiPrefs } from "../hooks/useUiPrefs.js";
import { UI_PREFS_OFFICES_V1 } from "../../services/portfolioStatePersistence.js";
import { LocationsGrid } from "../../features/locations/LocationsGrid.jsx";
import { LocationsMap } from "../../features/locations/LocationsMap.jsx";
import { hasCoords } from "../../features/locations/OfficeMapFoundation.jsx";
// Task #9085: LocationDrawerV2 (400px peek) is retained in ./LocationDrawerV2.jsx for rollback but no longer mounted.
import { getBands } from "../../services/benchmarkBandsService.js";
import {
  computeOfficeHorizonHeadroom,
  getDistinctMarketCount,
} from "../../features/plan/marketsService.js";
import {
  computeOfficeSpaceFit,
  SPACE_FIT_COLOR,
  SPACE_FIT_BG,
  SPACE_FIT_BORDER,
  SPACE_FIT_SEVERITY,
  SPACE_FIT_LABEL,
  SPACE_FIT_VERDICT_OVER,
  SPACE_FIT_VERDICT_DENSE,
  SPACE_FIT_VERDICT_UNDER,
  SPACE_FIT_VERDICT_RIGHT,
  SPACE_FIT_VERDICT_TIGHT,
} from "../../services/officeSeatFitPresentation.js";
import { SeatFitKey } from "../../components/SeatFitKey.jsx";
import { seatFitBands } from "../../services/seatFit.js";
import { useSpacePolicy } from "../../services/spacePolicyStore.js";
import { HowWeDefineThis } from "../../components/SpacePolicyDefinitions.jsx";
import { SeatDensityBar, officeSeatDensity } from "../../components/SeatDensityBar.jsx";
import { HeadingTooltip } from "../../components/HeadingTooltip.jsx";
import { LIGHT_HEADER_TOOLTIP_STYLE, SpaceFitHeaderCopy } from "../../components/ColumnHeaderExplanation.jsx";
import { DENSE_SF_PER_SEAT } from "../../services/seatFit.js";
import { getCapacity } from "../../data/marketsClient.js";
import { getEffectiveSpacePolicy } from "../../features/plan/policyClient.js";
import { getColCellHighlight } from "../utils/sortColumnHighlight.js";
import { CollectionSortHeader, CollectionSortCaret } from "../components/CollectionSortHeader.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { governedNavEnabled } from "../../app/governedNav.js";
import { assignmentPercentage } from "../../../shared/assignmentPercentage.js";
import { knownTotal, coverageText } from "../../services/seatAccounting.js";
import { PartialMarker } from "../../components/PartialTotal.jsx";
import { OverflowButton, useListOverflow } from "../components/ListOverflow.jsx";
import { useListHeaderStuck } from "../hooks/useListHeaderStuck.js";
import { useCollectionCellTitles } from "../hooks/useCollectionCellTitles.js";
import { OFFICE_GRID_FALLBACK, useOfficeListGeometry } from "./useOfficeListGeometry.js";

// Initialised once per page-load — close enough for headroom display purposes.
const TODAY = new Date();

// ── computePortfolioStats — inlined to avoid circular dependency with PortfolioView.jsx ──
// CAPACITY-DEFINITION-FIX: callers should pass a USABLE_CAPACITY_STAGES-filtered
// array so the returned totals only cover operational + dormant offices. This function
// is intentionally stage-agnostic; the filter lives at the call site (see portfolioUsable
// useMemo below) so alert-chip consumers can still call it with the full filtered list.
export function computePortfolioStats(locations) {
  let officeCount = 0;
  let headcount = 0;
  let totalSqft = 0;
  let knownSqftCount = 0;
  let totalCapacity = 0;
  let missingMaxCapacity = 0;
  let missingInstalledCapacity = 0;
  const stageCounts = { operational: 0, dormant: 0, build: 0, vacated: 0, closed: 0 };
  for (const loc of locations) {
    if (loc.stage === "draft") continue;
    officeCount += 1;
    headcount += Number(loc.latestHeadcount?.totalCount) || 0;
    if (loc.stage in stageCounts) stageCounts[loc.stage] += 1;
    const sqft = getDisplayedSqft(loc, null);
    totalSqft += sqft ?? 0;
    if (sqft != null) knownSqftCount += 1;
    totalCapacity += Number(loc.installedCapacity) || 0;
    // MAX-CAPACITY-BACKFILL-S1: track offices with no max_capacity on file
    // so Headroom (this table + What-If Densify) can flag the data gap
    // instead of silently showing "—" everywhere.
    if (loc.maxCapacity == null) missingMaxCapacity += 1;
    // DATA-HEALTH-STRIP-S1: second sample completeness signal, proving the
    // strip stacks multiple warnings — offices with no installed_capacity
    // on file understate the "Total seats" KPI silently.
    if (loc.installedCapacity == null) missingInstalledCapacity += 1;
  }
  return { officeCount, headcount, totalSqft, knownSqftCount, totalCapacity, missingMaxCapacity, missingInstalledCapacity, stageCounts };
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DENSITY_COMFORTABLE = "comfortable";
const DENSITY_COMPACT      = "compact";

const VIEW_LIST  = "list";
const VIEW_TILES = "tiles";
const VIEW_MAP   = "map";

// ── Saved view presets ────────────────────────────────────────────────────────

const SAVED_VIEW_PRESETS = [
  {
    id: "active",
    label: "Active",
    filters: { stage: ["operational", "dormant", "build"] },
  },
  {
    id: "all_active",
    label: "All active",
    filters: { stage: ["operational", "dormant", "build"] },
  },
  { id: "emea",     label: "EMEA",     filters: { region: ["EMEA"] } },
  { id: "amer",     label: "AMER",     filters: { region: ["AMER"] } },
  { id: "at_risk",  label: "At risk",  filters: { space_fit_verdict: [SPACE_FIT_VERDICT_OVER, SPACE_FIT_VERDICT_TIGHT] } },
  { id: "in_build", label: "In-build", filters: { stage: ["build"] } },
];

const DEFAULT_SELECTIONS = SAVED_VIEW_PRESETS[0].filters;

function fmtRecentUtilMonth(value) {
  if (!value) return "—";
  const raw = String(value).includes("T") ? String(value).split("T")[0] : String(value);
  const date = new Date(`${raw.slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? raw.slice(0, 7)
    : date.toLocaleString("en-US", { month: "short", year: "numeric" });
}

// DEFAULT-SORT-AND-SCOPE-PER-OBJECT-VIEW: first-load scope default, distinct
// from the "Active" saved-view preset above (which also includes Build).
// Isolates Operational + Dormant only. Used ONLY as the initial state below —
// a saved sessionStorage preference (including one that matches the "Active"
// preset) always wins over this on subsequent loads.
const DEFAULT_SCOPE_SELECTIONS = { stage: ["operational", "dormant"] };

// Default state shape persisted to sessionStorage via useViewStateSession.
// Changing the key constant in portfolioStatePersistence.js automatically
// invalidates old blobs — bump the key version there, not here.
// sortKey "sqft" (desc) shows the largest offices first — the most natural
// neutral ordering for a portfolio manager scanning the fleet.
const DEFAULT_V2_VIEW_STATE = {
  selections: DEFAULT_SCOPE_SELECTIONS,
  viewMode: VIEW_LIST,
  density: DENSITY_COMFORTABLE,
  search: "",
  sortKey: "sqft",
  sortDir: "desc",
  sortKey2: null,
  sortDir2: "asc",
  userHasSetSort: false,
};

// ── Sort helpers ──────────────────────────────────────────────────────────────

// Columns where the natural first-click direction is descending (highest first).
const NUMERIC_SORT_KEYS = new Set(["space_fit_verdict", "sfPerSeat", "sqft", "seats", "people", "peak", "assignedPct", "hub", "headroom"]);

// Sort fields offered by the shared toolbar SortControl (LIST-SORT-ROLLOUT-S1).
// "Office" is the default (A→Z). Order mirrors the column headers exactly
// (OFFICES-SORT-COLUMNS-S1) — Hub and M&A added, "Next lease event" removed.
export const OFFICES_SORT_FIELDS = [
  { value: "location",          label: "Office" },
  { value: "hub",               label: "Hub",         numeric: true },
  { value: "stage",             label: "Stage" },
  { value: "mAndA",             label: "M&A" },
  { value: "space_fit_verdict", label: "Space Fit",   numeric: true },
  { value: "sfPerSeat",         label: "SF / Seat",   numeric: true },
  { value: "sqft",              label: "Sq Ft",       numeric: true },
  { value: "seats",             label: "Seats",       numeric: true },
  { value: "people",            label: "People",      numeric: true },
  { value: "peak",              label: "Peak count",  numeric: true },
  { value: "headroom",          label: "Expansion",   numeric: true },
];

// Sentinel returned by getSortValue when a cell has no data (null / "—").
// sortRows detects this and always moves such rows to the bottom, independent
// of sort direction.
const BLANK = Symbol("blank");

export function getSortValue(loc, sortKey) {
  switch (sortKey) {
    case "location": {
      const v = (loc.market ?? "").toLowerCase();
      return v || BLANK;
    }
    case "space_fit_verdict": {
      if (!USABLE_CAPACITY_STAGES.includes(loc.stage)) return BLANK;
      return getSortValue(loc, "assignedPct");
    }
    case "sfPerSeat":
      return officeSeatDensity(loc) ?? BLANK;
    case "stage":
      return STAGE_ORDER[(loc.stage ?? "").trim().toLowerCase()] ?? 999;
    case "region": {
      const v = (loc.region ?? "").toLowerCase();
      return v || BLANK;
    }
    case "sqft": {
      const v = getDisplayedSqft(loc, null);
      // VACATED-DETAIL-RETENTION (O-27): sort by the displayed value — retained
      // sqft for vacated/retired, derived sqft for active offices.
      const displayV = (loc.stage === "vacated" || loc.stage === "retired") && loc.displayInstalledSqft != null
        ? loc.displayInstalledSqft
        : v;
      return displayV != null ? displayV : BLANK;
    }
    case "seats": {
      // VACATED-SEATS-DISPLAY-CONSISTENCY (O-24): sort by the same value shown
      // in the cell — displayInstalledCapacity for vacated offices (real pre-exit
      // count), installedCapacity for all others.
      const sv = loc.displayInstalledCapacity ?? loc.installedCapacity;
      return sv != null ? Number(sv) : BLANK;
    }
    case "people": {
      const c = loc.latestHeadcount?.totalCount;
      // GridRow renders "—" when people is null OR <= 0, so mirror that here.
      const n = c != null ? Number(c) : null;
      return n != null && n > 0 ? n : BLANK;
    }
    case "peak": {
      const p = loc.recentPeakBadges;
      return p != null ? Number(p) : BLANK;
    }
    case "assignedPct": {
      const seats = loc.installedCapacity != null ? Number(loc.installedCapacity) : null;
      const hc = loc.latestHeadcount?.totalCount != null ? Number(loc.latestHeadcount.totalCount) : null;
      return Number.isFinite(seats) && Number.isFinite(hc) && seats > 0 ? hc / seats : BLANK;
    }
    case "mAndA": {
      const v = (loc.mAndA ?? "").toLowerCase();
      return v || BLANK;
    }
    case "hub":
      return loc.isHub ? 1 : BLANK;
    case "headroom": {
      const maxCapacity = loc?.maxCapacity;
      if (typeof maxCapacity !== "number") return BLANK;
      const installed = typeof loc?.installedCapacity === "number" ? loc.installedCapacity : 0;
      return maxCapacity - installed;
    }
    default:
      return 0;
  }
}

export function sortRows(rows, sortKey, sortDir, sortKey2 = null, sortDir2 = "asc") {
  const dir = sortDir === "desc" ? -1 : 1;
  const dir2 = sortDir2 === "desc" ? -1 : 1;

  // Partition: rows with a real value vs. rows that have no data (BLANK).
  // Blank rows are always appended at the end regardless of sort direction.
  const real = [];
  const blank = [];
  for (const row of rows) {
    if (getSortValue(row, sortKey) === BLANK) blank.push(row);
    else real.push(row);
  }

  real.sort((a, b) => {
    const va = getSortValue(a, sortKey);
    const vb = getSortValue(b, sortKey);
    // Stable tiebreaker shared by all paths: A–Z by location name, case-insensitive,
    // matching the normalisation used by getSortValue("location").
    const nameA = (a.market ?? "").toLowerCase();
    const nameB = (b.market ?? "").toLowerCase();

    function tiebreak() {
      // Explicit "Then by" from the toolbar SortControl wins when set.
      if (sortKey2) {
        const va2 = getSortValue(a, sortKey2);
        const vb2 = getSortValue(b, sortKey2);
        if (va2 === BLANK && vb2 !== BLANK) return 1;
        if (vb2 === BLANK && va2 !== BLANK) return -1;
        if (va2 !== BLANK && vb2 !== BLANK) {
          const secondary = typeof va2 === "string" && typeof vb2 === "string"
            ? va2.localeCompare(vb2) * dir2
            : (va2 - vb2) * dir2;
          if (secondary !== 0) return secondary;
        }
      } else if (sortKey === "stage") {
        // Legacy default: within the same stage bucket, larger sq-ft first
        // (existing column-header click behaviour, preserved when no explicit
        // "Then by" has been chosen).
        const sqftA = getDisplayedSqft(a, null) ?? -Infinity;
        const sqftB = getDisplayedSqft(b, null) ?? -Infinity;
        const sqftDiff = sqftB - sqftA;
        if (!Number.isNaN(sqftDiff) && sqftDiff !== 0) return sqftDiff;
      }
      return nameA.localeCompare(nameB) || String(a.id).localeCompare(String(b.id));
    }

    if (typeof va === "string" && typeof vb === "string") {
      const primary = va.localeCompare(vb) * dir;
      if (primary !== 0) return primary;
      return tiebreak();
    }
    const primary = (va - vb) * dir;
    if (primary !== 0) return primary;
    return tiebreak();
  });

  // Apply the same secondary/name/ID contract inside the missing-value group.
  if (sortKey2 && sortKey2 !== sortKey) {
    blank.splice(0, blank.length, ...sortRows(blank, sortKey2, sortDir2));
  } else {
    blank.sort((a, b) => String(a.market ?? "").localeCompare(String(b.market ?? ""), undefined, { sensitivity: "base" })
      || String(a.id).localeCompare(String(b.id)));
  }
  return [...real, ...blank];
}

function selectionsEqual(a, b) {
  b = normalizeCheckedSelections(FILTER_DIMENSIONS, b, DEFAULT_SCOPE_SELECTIONS);
  const keysA = Object.keys(a).filter((k) => a[k]?.length > 0);
  const keysB = Object.keys(b).filter((k) => b[k]?.length > 0);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((k) => {
    const arrA = [...(a[k] ?? [])].sort();
    const arrB = [...(b[k] ?? [])].sort();
    return arrA.length === arrB.length && arrA.every((v, i) => v === arrB[i]);
  });
}

// ── Filter logic ──────────────────────────────────────────────────────────────

export function applyV2Filters(locations, selections, checked = false, countGroup = null) {
  if (checked) {
    selections = checkedPredicateSelections(FILTER_DIMENSIONS, selections, false, countGroup);
    if (!selections) return [];
  }
  return locations.filter((loc) => {
    if (selections.stage?.length > 0 && !selections.stage.includes(loc.stage)) return false;
    if (selections.region?.length > 0 && !selections.region.includes(loc.region)) return false;
    if (selections.space_fit_verdict?.length > 0) {
      // CAPACITY-DEFINITION-FIX: build/vacated offices have no Space Fit verdict — they
      // are not scored even if seats+people data is present. Matches SpaceFitCell below.
      const { verdict } = USABLE_CAPACITY_STAGES.includes(loc.stage)
        ? computeOfficeSpaceFit(loc)
        : { verdict: null };
      if (!verdict) {
        if (!selections.space_fit_verdict.includes(FILTER_NULL_VALUE)) return false;
      } else if (!selections.space_fit_verdict.includes(verdict)) return false;
    }
    if (selections.lease?.length > 0) {
      const types = getActiveLeaseTypes(loc);
      if (types.length === 0) {
        if (!selections.lease.includes(FILTER_NULL_VALUE)) return false;
      } else if (!types.some((t) => selections.lease.includes(t))) return false;
    }
    if (selections.mAndA?.length > 0) {
      if (!loc.mAndA) {
        if (!selections.mAndA.includes(FILTER_NULL_VALUE)) return false;
      } else if (!selections.mAndA.includes(loc.mAndA)) return false;
    }
    return true;
  });
}

// OFFICES-RECONCILIATION-ALERT: exported for unit testing.
// Returns a Set of location IDs that satisfy at least one reconciliation trigger:
//   Trigger 1 — Operational office with 0 headcount (emptied but not closed).
//   Trigger 2 — Schedule-row HC diverges from the Workday snapshot HC.
// Both are read-only detections; no mutations are performed here.
export function computeReconFlaggedIds(locations) {
  const ids = new Set();
  for (const loc of locations) {
    const t1 = loc.latestHeadcount?.totalCount === 0 && loc.stage === "operational";
    const t2 = loc.headcountSourceConflict != null;
    if (t1 || t2) ids.add(loc.id);
  }
  return ids;
}

// VACATED-OFFICE-ASSIGNED-HC-FLAG (O-28): exported for unit testing.
// Returns a Set of location IDs where the office is vacated or retired (closed)
// AND still has Workday headcount > 0. These offices should be flagged with an
// amber dot in the People column and a toolbar alert chip directing users to
// reassign the headcount in Workday.
// Uses loc.workdayHc (the raw Workday count preserved server-side before the
// occupancy gate zeroes latestHeadcount.totalCount) so the flag fires correctly
// even though the operational HC is zeroed for vacated/closed offices.
export function computeVacatedHcFlaggedIds(locations) {
  const ids = new Set();
  for (const loc of locations) {
    // Mirror OCCUPANCY_ZERO_STAGES on the server: "closed" and "vacated".
    const isInactive = loc.stage === "vacated" || loc.stage === "closed";
    // workdayHc is only emitted by the server when the stage is gated, so
    // a non-zero value here is always a data-quality signal worth surfacing.
    const hc = loc.workdayHc ?? 0;
    if (isInactive && hc > 0) ids.add(loc.id);
  }
  return ids;
}

// ── Filter dimensions config ──────────────────────────────────────────────────

// Sentinel used as the "no value" option value in facets that can have blank rows.
// Checking this option in a group includes locations with no value for that dimension.
export const FILTER_NULL_VALUE = "__none__";

const FILTER_DIMENSIONS = [
  {
    id: "stage",
    label: "Stage",
    options: [
      { value: "draft",       label: STAGES.draft.label       },
      { value: "build",       label: STAGES.build.label       },
      { value: "operational", label: STAGES.operational.label },
      { value: "dormant",     label: STAGES.dormant.label     },
      { value: "vacated",     label: STAGES.vacated.label     },
      { value: "closed",      label: "Retired"                  },
    ],
  },
  {
    id: "region",
    label: "Region",
    options: REGIONS.map((r) => ({ value: r, label: r })),
  },
  {
    id: "lease",
    label: "Lease",
    options: [
      ...LEASE_TYPES.map((t) => ({ value: t, label: LEASE_DISPLAY[t] })),
      { value: FILTER_NULL_VALUE, label: "No active lease" },
    ],
  },
  {
    id: "mAndA",
    label: "M&A",
    options: [
      ...M_AND_A_TAGS.map((t) => ({ value: t.id, label: t.name })),
      { value: FILTER_NULL_VALUE, label: "Non-M&A" },
    ],
  },
  {
    id: "space_fit_verdict",
    label: "Space Fit",
    options: [
      ...seatFitBands().map(({ label }) => ({ value: label, label })),
      { value: FILTER_NULL_VALUE,       label: "No data"       },
    ],
  },
];

export function migrateSpaceFitSelection(values) {
  if (!Array.isArray(values)) return values;
  const legacy = values.includes("Dense");
  if (legacy) return FILTER_DIMENSIONS.find(d => d.id === "space_fit_verdict").options.map(o => o.value);
  return [...new Set(values.map(v => v === "Over-Capacity" ? "Over-Cap" : v))];
}
export function migrateOfficePrefs(state) {
  const oldFit = state.selections?.space_fit_verdict;
  const selections = state.selections ? { ...state.selections, space_fit_verdict: migrateSpaceFitSelection(oldFit) } : state.selections;
  const primary = state.sortKey === "assignedPct" ? "space_fit_verdict"
    : ["region", "nextLeaseEvent"].includes(state.sortKey) ? DEFAULT_V2_VIEW_STATE.sortKey : state.sortKey;
  const secondary = state.sortKey2 === "assignedPct" ? "space_fit_verdict" : state.sortKey2;
  return {
    ...state,
    // The lease-event sort was removed with its report. Do not retain an
    // invisible sort in older preferences or fetch retired data to support it.
    ...(state.sortKey === "assignedPct" ? { sortKey: "space_fit_verdict" } : {}),
    ...(["nextLeaseEvent", "region"].includes(state.sortKey) ? {
      sortKey: DEFAULT_V2_VIEW_STATE.sortKey,
      sortDir: DEFAULT_V2_VIEW_STATE.sortDir,
      userHasSetSort: false,
    } : {}),
    ...(state.sortKey2 === "assignedPct" ? { sortKey2: "space_fit_verdict" } : {}),
    ...(["nextLeaseEvent", "region"].includes(state.sortKey2) ? { sortKey2: null } : {}),
    ...(primary === secondary ? { sortKey2: null } : {}),
    filterVersion: CHECKED_FILTER_VERSION,
    selections: normalizeCheckedSelections(FILTER_DIMENSIONS, selections, DEFAULT_SCOPE_SELECTIONS, {
      legacy: state.filterVersion !== CHECKED_FILTER_VERSION,
    }),
  };
}


// ── URL param codec (OFFICES-URL-PARAMS) ─────────────────────────────────────
//
// Serialises/deserialises Offices display state to/from URL query params so
// any Offices view is shareable and bookmarkable.
//
// Param → viewState mapping:
//   view     → viewMode  (tiles|map only; list is URL default — omit to get bare /offices)
//   stage, region, health, lease, mAndA → selections[dim] (comma-separated multi-value)
//   sort     → sortKey   (sort field key)
//   sortDir  → sortDir   (asc|desc)
//   q        → search    (search text)
//   density  → density   (compact only; omit when comfortable)
//   peek     → drawerLoc (location ID of open peek drawer; omit when closed)
//
// Unknown params are ignored by decode but preserved on writes. All URL writes
// use React Router replace, retaining the hash and history state.

const URL_FILTER_DIMS = ["stage", "region", "space_fit_verdict", "lease", "mAndA"];

export function mergeOfficesSearch(search, encoded) {
  const params = new URLSearchParams(search);
  for (const key of [...URL_FILTER_DIMS, "view", "sort", "sortDir", "q", "density", "peek", "filters"]) params.delete(key);
  for (const [key, value] of new URLSearchParams(encoded)) params.set(key, value);
  return params.toString();
}

/**
 * Decode window.location.search into a URL param bag.
 * Returns null when no recognised offices params are found.
 * Never throws.
 */
export function decodeOfficesParams(search) {
  try {
    const p = new URLSearchParams(search);
    const bag = {};
    let hasAny = false;

    const view = p.get("view");
    if (view && (view === VIEW_LIST || view === VIEW_TILES || view === VIEW_MAP)) {
      bag.view = view;
      hasAny = true;
    }

    for (const dim of URL_FILTER_DIMS) {
      const raw = p.get(dim);
      if (raw !== null) {
        bag[dim] = raw === "" && p.get("filters") !== "checked"
          ? allOptionValues(FILTER_DIMENSIONS.find(({ id }) => id === dim))
          : raw.split(",").filter(Boolean);
        hasAny = true;
      } else if (p.get("filters") === "checked") {
        bag[dim] = [...(DEFAULT_SCOPE_SELECTIONS[dim] ?? allOptionValues(FILTER_DIMENSIONS.find(({ id }) => id === dim)))];
        hasAny = true;
      }
    }
    if (bag.space_fit_verdict) bag.space_fit_verdict = migrateSpaceFitSelection(bag.space_fit_verdict);

    const sort = p.get("sort");
    if (sort && !["nextLeaseEvent", "region"].includes(sort)) {
      bag.sort = sort === "assignedPct" ? "space_fit_verdict" : sort;
      hasAny = true;
    }

    const sortDir = p.get("sortDir");
    if (sortDir === "asc" || sortDir === "desc") {
      bag.sortDir = sortDir;
    }

    const q = p.get("q");
    if (q != null && q !== "") {
      bag.q = q;
      hasAny = true;
    }

    const density = p.get("density");
    if (density === DENSITY_COMPACT) {
      bag.density = DENSITY_COMPACT;
      hasAny = true;
    }

    const peek = p.get("peek");
    if (peek) {
      bag.peek = peek;
      hasAny = true;
    }

    return hasAny ? bag : null;
  } catch {
    return null;
  }
}

/**
 * Merge a URL param bag (from decodeOfficesParams) into a viewState object.
 * URL params win over sessionStorage values for every key they touch.
 */
function applyUrlBagToViewState(state, bag) {
  const next = { ...state };

  if (bag.view) {
    next.viewMode = bag.view;
  }

  const selOverrides = {};
  let selChanged = false;
  for (const dim of URL_FILTER_DIMS) {
    if (bag[dim] !== undefined) {
      selOverrides[dim] = bag[dim];
      selChanged = true;
    }
  }
  if (selChanged) {
    next.selections = { ...state.selections, ...selOverrides };
  }

  if (bag.sort !== undefined) {
    next.sortKey = bag.sort;
    next.userHasSetSort = true;
    // Use explicit direction from URL, or derive the natural default for the key.
    next.sortDir = bag.sortDir ?? (NUMERIC_SORT_KEYS.has(bag.sort) ? "desc" : "asc");
  }

  if (bag.q !== undefined) {
    next.search = bag.q;
  }

  if (bag.density !== undefined) {
    next.density = bag.density;
  }

  return next;
}

/**
 * Encode the current viewState + optional peek location ID into a URLSearchParams
 * query string (without the leading "?"). Returns an empty string when the state
 * matches the URL defaults (bare /offices).
 *
 * Encoding rules:
 *   view     — included for tiles/map; omit VIEW_LIST (URL default)
 *   filter dims — included when non-empty
 *   sort     — included when userHasSetSort is true
 *   q        — included when non-empty
 *   density  — included when compact
 *   peek     — included when non-null
 */
function _arraysEqualAsSet(a, b) {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((v) => setA.has(v));
}

export function encodeOfficesParams(vs, peekId) {
  const p = new URLSearchParams();

  // view: include for tiles/map; omit for list (URL default)
  if (vs.viewMode && vs.viewMode !== VIEW_LIST) {
    p.set("view", vs.viewMode);
  }

  // filter dims: comma-separated; omit when empty OR when equal to the
  // documented baseline so the URL stays clean at rest.
  for (const dim of URL_FILTER_DIMS) {
    const vals = vs.selections?.[dim] ?? [];
    const defaultVals = DEFAULT_SCOPE_SELECTIONS[dim] ?? allOptionValues(FILTER_DIMENSIONS.find(({ id }) => id === dim));
    if (vs.selections?.[dim] !== undefined && !_arraysEqualAsSet(vals, defaultVals)) {
      p.set(dim, vals.join(","));
      if (vs.filterVersion === CHECKED_FILTER_VERSION) p.set("filters", "checked");
    }
  }

  // sort: only when the user has explicitly changed it from the default
  if (vs.userHasSetSort && vs.sortKey) {
    p.set("sort", vs.sortKey);
    if (vs.sortDir && vs.sortDir !== "asc") {
      p.set("sortDir", vs.sortDir);
    }
  }

  // search
  if (vs.search) {
    p.set("q", vs.search);
  }

  // density: only when compact
  if (vs.density === DENSITY_COMPACT) {
    p.set("density", DENSITY_COMPACT);
  }

  // peek drawer
  if (peekId) {
    p.set("peek", peekId);
  }

  if (vs.filterVersion === CHECKED_FILTER_VERSION && p.size > 0) p.set("filters", "checked");
  return p.toString();
}

// ── Grid table columns ────────────────────────────────────────────────────────
//
// Resolved once for both header and body; surplus belongs to the data, not
// Office or an unnamed trailing gutter. Fallback also pairs the loading header.
const GRID_COLS = `var(--office-tracks, ${OFFICE_GRID_FALLBACK})`;
const GRID_MIN_W = "var(--office-min-width, 1154px)";

// One entry per data column; no named column for row actions.
// align values mirror the textAlign on data cells in GridRow.
const COL_HEADERS = [
  { key: "location",          label: "Office",     align: "left", sortable: true  },
  { key: "hub",               label: "Hub",         align: "center", sortable: true, tooltip: true },
  { key: "stage",             label: "Stage",       align: "center", sortable: true, tooltip: true },
  { key: "mAndA",             label: "M&A",         align: "center", sortable: true, tooltip: true },
  { key: "space_fit_verdict", label: "Space Fit",   align: "center", sortable: true, tooltip: true },
  { key: "sfPerSeat",         label: "SF / Seat",   align: "center", sortable: true, tooltip: true },
  { key: "sqft",              label: "Sq ft",       align: "right", sortable: true  },
  { key: "seats",             label: "Seats",       align: "right", sortable: true  },
  { key: "people",            label: "People",      align: "right", sortable: true, tooltip: true },
  { key: "peak",              label: "Peak count",  align: "right", sortable: true  },
  { key: "headroom",          label: "Expansion",   align: "right", sortable: true, tooltip: true },
];

// ── SpaceFitHeaderPopover ─────────────────────────────────────────────────────
// Portal-based popover for the Space Fit column header. Portalled to
// document.body so it escapes the overflowX:hidden scroll-sync container and
// renders unclipped above the table. Coordinates are computed from the trigger
// button's bounding rect whenever `show` becomes true.

function SpaceFitHeaderPopover({ show, triggerRef, popupProps }) {
  if (!show) return null;
  return <HeadingTooltip triggerRef={triggerRef} role="tooltip"
      id="space-fit-header-tooltip-panel"
      data-testid="space-fit-header-tooltip-panel"
      {...popupProps}
      onKeyDown={event => {
        if (event.key === "Escape") triggerRef.current?.focus({ preventScroll: true });
        popupProps?.onKeyDown?.(event);
      }}
      style={LIGHT_HEADER_TOOLTIP_STYLE}>
      <SpaceFitHeaderCopy />
    </HeadingTooltip>;
}

const HEADER_TOOLTIP_TEXT = {
  sfPerSeat: "Rentable square feet divided by installed seats. The track runs from 0 to 400; the blue typical range is 150 to 250. The amber tick follows the effective Space Policy floor.",
  hub: "Hub offices are our anchor locations: population centers and centers of gravity with full-service on-site IT support and other services. The diamond marks a hub; not every office is one.",
  stage: "Where the office sits in its lifecycle: Draft (pre-signature scaffolding), In-Build (secured, fit-out underway), Operational (open and in use), Dormant (temporarily closed, expected to reopen), Vacated (space no longer occupied), or Retired (office permanently closed).",
  mAndA: "Offices that came in through a merger or acquisition, tagged with the deal or acquired business (e.g. Treasury, Custody). A dash means the office was established organically.",
  people: "Headcount assigned to this office, including both full-time employees and contractors.",
  headroom: "Expansion capacity - desks that can be added without taking new space (Max density − seats).",
  assignedPct: "Assignment — assigned headcount divided by installed seats (the usable seats displayed in the Seats column).",
};

function HeaderTextTooltip({ show, triggerRef, column }) {
  if (!show) return null;
  return <HeadingTooltip triggerRef={triggerRef}
      id={`${column}-header-tooltip-panel`}
      role="tooltip"
      data-testid={`${column === "assignedPct" ? "assigned-pct" : column}-header-tooltip-panel`}
      style={LIGHT_HEADER_TOOLTIP_STYLE}
    >
      {HEADER_TOOLTIP_TEXT[column]}
    </HeadingTooltip>;
}

// ── PortalDotTooltip ─────────────────────────────────────────────────────────
// Portal-based tooltip for row-level dots/pills inside the overflowX:hidden
// scroll container. Uses position:fixed + getBoundingClientRect() so it is
// never clipped by an ancestor overflow. Pattern matches HeadroomHeaderTooltip
// above. Positions the tooltip above the trigger, centred horizontally.

function PortalDotTooltip({ show, triggerRef, children, interactive = false, ...rest }) {
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (show && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ top: rect.top, left: rect.left + rect.width / 2 });
    }
  }, [show, triggerRef]);

  if (!show) return null;

  return createPortal(
    <span
      role="tooltip"
      {...rest}
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        transform: "translate(-50%, calc(-100% - 5px))",
        background: T.tooltip.bg,
        color: T.tooltip.fg,
        borderRadius: 6,
        padding: "6px 10px",
        boxShadow: T.tooltip.shadow,
        fontSize: T.fsXs,
        fontWeight: T.fwRegular,
        lineHeight: 1.5,
        whiteSpace: "nowrap",
        pointerEvents: interactive ? "auto" : "none",
        zIndex: 9999,
      }}
    >
      {children}
    </span>,
    document.body
  );
}

// ── Space Fit cell ────────────────────────────────────────────────────────────

// Minimum pill width for Space Fit badges — measured as the max width across all
// short labels (the abbreviated forms used in this tight column) at the pill's
// exact font spec (T.fwSemibold / T.fsXs / sans-serif), plus 2 × 10 px horizontal
// padding. Falls back to 100 px for SSR / headless.
const SPACE_FIT_PILL_MIN_W = (() => {
  try {
    if (typeof document === "undefined") return 100;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return 100;
    ctx.font = `${T.fwSemibold} ${T.fsXs}px sans-serif`;
    const labels = ["Right-Sized", "Over-Cap", "Dense", "Under-Used"];
    return Math.max(...labels.map(l => Math.ceil(ctx.measureText(l).width))) + 20;
  } catch {
    return 100;
  }
})();

// Inline micro-label shown inside the "—" cell for non-usable-stage offices.
// Keeps the reason self-contained at the row level without requiring a header
// tooltip hover. Stages not listed here show a plain "—" with no note.
const SPACE_FIT_STAGE_NOTE = {
  build:   "In build",
  vacated: "Vacated",
};

function OfficeDensityCell({ loc, extraStyle }) {
  const { baseline } = useSpacePolicy();
  return <div style={{ padding: "0 8px", minWidth: 0, display: "flex", justifyContent: "center", ...extraStyle }}>
    <SeatDensityBar value={officeSeatDensity(loc)}
      floor={baseline?.sfFloor} width={80} data-testid={`v2-density-${loc.id}`} />
  </div>;
}

function SpaceFitCell({ loc, density, extraStyle = {} }) {
  const intent = useHoverIntent({ interactive: true });
  const [pinned,  setPinned]  = React.useState(false);
  const pillRef = useRef(null);

  // CAPACITY-DEFINITION-FIX: build/vacated offices are not scored — they must not
  // receive an Over-Capacity badge even when their seats+people data is present.
  const { verdict, status, disclosure } = computeOfficeSpaceFit(loc);
  const assignedPct = assignmentPercentage(loc.installedCapacity, loc.latestHeadcount?.totalCount);

  // Dismiss pinned tooltip when the user clicks elsewhere in the document.
  React.useEffect(() => {
    if (!pinned) return;
    function onDocClick(e) {
      if (!e.target.closest(`[data-testid="v2-space-fit-pill-${loc.id}"]`)) {
        setPinned(false);
      }
    }
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [pinned, loc.id]);

  if (!verdict) {
    const stageNote = SPACE_FIT_STAGE_NOTE[loc.stage];
    return (
      <div
        data-testid={`v2-space-fit-${loc.id}`}
        style={{ padding: "0 14px", display: "flex", alignItems: "center", justifyContent: "center", ...extraStyle }}
      >
        <span data-testid={stageNote && density !== "compact" ? `v2-space-fit-note-${loc.id}` : undefined} title={stageNote || "Space Fit unavailable"}
          style={{ color: "var(--v2-light)", whiteSpace: "nowrap" }}>{status === "partial" ? "Partial" : status === "no_seats" ? "No seats" : status === "loading" ? "Loading…" : status === "error" ? "Read failed" : "—"}</span>
      </div>
    );
  }

  const color      = SPACE_FIT_COLOR[verdict];
  const bg         = SPACE_FIT_BG[verdict];
  const border     = SPACE_FIT_BORDER[verdict];
  const shortLabel = SPACE_FIT_LABEL[verdict].short;
  const fullLabel  = SPACE_FIT_LABEL[verdict].full;
  // Only show tooltip when the pill is abbreviated (Over-Cap ≠ Over-Capacity).
  const isAbbrev   = shortLabel !== fullLabel;
  const showTip    = intent.open || pinned;

  return (
    <div
      data-testid={`v2-space-fit-${loc.id}`}
      style={{ padding: "0 6px", display: "flex", alignItems: "center", justifyContent: "center", minWidth: 0, ...extraStyle }}
    >
      <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <div
          ref={pillRef}
          data-testid={`v2-space-fit-pill-${loc.id}`}
          tabIndex={0}
          title={`${fullLabel}: ${disclosure}`}
          aria-label={`${fullLabel}: ${disclosure}`}
          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 6, height: density === "compact" ? 20 : 22, lineHeight: 1, padding: "0 10px", fontSize: density === "compact" ? T.fsXxs : T.fsXs, fontWeight: T.fwSemibold, whiteSpace: "nowrap", background: bg, color, minWidth: SPACE_FIT_PILL_MIN_W, cursor: isAbbrev ? "pointer" : "help" }}
          {...intent.triggerProps}
          onClick={(e) => { if (isAbbrev) { e.stopPropagation(); setPinned((p) => !p); } }}
        >
          {shortLabel}
        </div>
        <span data-testid={`v2-assigned-pct-${loc.id}`} style={{ color: T.mid, fontSize: T.fsXxs, fontVariantNumeric: "tabular-nums" }}>
          {assignedPct != null ? `${assignedPct}%` : "—"}
        </span>
        <PortalDotTooltip
          interactive
          {...intent.popupProps}
          show={showTip}
          triggerRef={pillRef}
          data-testid={`v2-space-fit-pill-tooltip-${loc.id}`}
        >
          {fullLabel}
          <div>{disclosure}</div>
          <HowWeDefineThis />
        </PortalDotTooltip>
      </div>
    </div>
  );
}

function PeakCell({ loc, extraStyle }) {
  const hasPeak = loc.recentPeakBadges != null;
  const reportingMonth = reportingMonthKey(loc.reportingPeriod?.month);
  const peakMonth = reportingMonthKey(loc.recentUtilMonth);
  const isFallbackMonth = hasPeak && reportingMonth && peakMonth && peakMonth < reportingMonth;
  const fallbackMonthLabel = isFallbackMonth
    ? formatReportingMonth(loc.recentUtilMonth)?.replace(/\s+\d{4}$/, "")
    : null;

  return (
    <div
      data-testid={`v2-peak-col-${loc.id}`}
      style={{ padding: "0 14px 0 6px", textAlign: "right", color: "var(--v2-mid)", fontSize: T.fsBase, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", ...extraStyle }}
    >
      <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4, whiteSpace: "nowrap" }}>
        {hasPeak ? (
          <>
            <span data-testid={`v2-peak-value-${loc.id}`}>
              {Math.round(loc.recentPeakBadges).toLocaleString()}
            </span>
            {fallbackMonthLabel && (
              <span
                data-testid={`v2-peak-fallback-month-${loc.id}`}
                title={`Peak count uses ${formatReportingMonth(loc.recentUtilMonth, { long: true })}`}
                style={{ color: "var(--v2-light)", fontSize: T.fsXxxs5, lineHeight: 1 }}
              >
                {fallbackMonthLabel}
              </span>
            )}
          </>
        ) : <span style={{ color: "var(--v2-light)" }}>—</span>}
      </div>
    </div>
  );
}

// ── Touch device detection ────────────────────────────────────────────────────
// Computed once at module load; used to keep the chevron always visible on touch screens.
const IS_TOUCH_DEVICE = typeof window !== "undefined" &&
  (navigator.maxTouchPoints > 0 || window.matchMedia?.("(pointer: coarse)").matches);

// ── Grid row ──────────────────────────────────────────────────────────────────

function GridRow({ loc, matchEvidence, onRowClick, drawerUrl, selected, density, isReconFlagged, isVacatedHcFlagged, activeSortKey, userHasSetSort }) {
  const [hovered, setHovered] = useState(false);
  const [nameHovered, setNameHovered] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);
  const vacatedHcIntent = useHoverIntent();
  const vacatedHcTip = vacatedHcIntent.open;
  const vacatedHcDotRef = useRef(null);
  const rowH = collectionRowHeight(density);

  const sqftVal = getDisplayedSqft(loc, null);
  // VACATED-DETAIL-RETENTION (O-27): prefer retained pre-exit sqft for display.
  const displaySqftVal = (loc.stage === "vacated" || loc.stage === "retired") && loc.displayInstalledSqft != null
    ? loc.displayInstalledSqft
    : sqftVal;
  const sqftStr = displaySqftVal != null ? fmtSqft(displaySqftVal) : null;
  const isSqftRetained = (loc.stage === "vacated" || loc.stage === "retired") && loc.displayInstalledSqft != null;
  const seats   = loc.installedCapacity != null ? Number(loc.installedCapacity) : null;
  const hc      = loc.latestHeadcount;
  const people  = hc?.totalCount != null ? Number(hc.totalCount) : null;

  // OFFICES-RECONCILIATION-ALERT: build the amber-indicator tooltip string for
  // this row. Trigger 2 takes precedence in the label when both apply.
  const reconTitle = isReconFlagged
    ? (loc.headcountSourceConflict
        ? `headcount (${loc.headcountSourceConflict.displayedCount}) doesn\u2019t match source (${loc.headcountSourceConflict.workdayCount}) \u2014 data issue`
        : `0 headcount as of ${hc?.snapshotDate ?? "unknown"} \u00b7 still Operational \u2014 close out?`)
    : null;

  const bg = selected ? T.rowActive : hovered ? T.hoverBg : "var(--v2-card)";

  const activate = (event) => activateCollectionRow(event, drawerUrl, () => onRowClick(loc));

  return (
    <div
      role="row"
      data-list-row=""
      tabIndex={0}
      data-testid={`v2-loc-row-${loc.id}`}
      className="office-collection-row collection-keyboard-row"
      onClick={activate}
      onAuxClick={activate}
      onKeyDown={activate}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        minWidth: GRID_MIN_W,
        alignItems: "center",
        background: bg,
        borderBottom: "1px solid var(--v2-hair)",
        cursor: "pointer",
        height: rowH,
        boxSizing: "border-box",
        minHeight: 0,
        transition: "background .1s",
      }}
    >
      {/* Name activation bubbles to the same drawer navigation as the row. */}
      <div
        style={{ padding: "0 14px", display: "flex", alignItems: "center", minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", ...getColCellHighlight("location", activeSortKey, userHasSetSort) }}
        data-collection-identity=""
      >
        <span
          role="button"
          tabIndex={0}
          data-row-name=""
          data-testid={`v2-loc-name-${loc.id}`}
          aria-label={`Open ${loc.market}${loc.locationAlias ? " · " + loc.locationAlias : ""}`}
          onMouseEnter={() => setNameHovered(true)}
          onMouseLeave={() => setNameHovered(false)}
          onFocus={() => setNameFocused(true)}
          onBlur={() => setNameFocused(false)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            maxWidth: "100%",
            overflow: "hidden",
            cursor: "pointer",
            borderRadius: 6,
            padding: "3px 7px",
            margin: "-3px -7px",
            background: (nameHovered || nameFocused) ? "var(--v2-tint)" : "transparent",
            outline: "none",
            transition: "background .12s",
          }}
        >
          <span data-collection-name="" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            <span
              style={{
                ...LIST_PRESENTATION.name,
              }}
            >
              {loc.market}
            </span>
            {loc.locationAlias && (
              <span style={LIST_PRESENTATION.secondary}>{" · "}{loc.locationAlias}</span>
            )}
          </span>
          <span
            aria-hidden="true"
            data-testid={`v2-loc-name-arrow-${loc.id}`}
            style={{
              flexShrink: 0,
              color: "var(--v2-light)",
              fontSize: T.fsSm,
              opacity: (nameHovered || nameFocused) ? 1 : 0,
              transform: (nameHovered || nameFocused) ? "translateX(0)" : "translateX(-4px)",
              transition: "opacity .12s, transform .12s",
            }}
          >
            ↗
          </span>
        </span>
        <IdentityMatchHint value={matchEvidence} inline />
      </div>

      {/* Hub */}
      <div
        data-testid={`v2-hub-col-${loc.id}`}
        style={{ padding: 0, textAlign: "center", ...getColCellHighlight("hub", activeSortKey, userHasSetSort) }}
      >
        {loc.isHub
          ? <span title="Hub" style={{ color: T.hubChipText, fontSize: T.fsSm, lineHeight: 1 }}>◆</span>
          : <span style={{ color: "var(--v2-light)" }}>—</span>
        }
      </div>

      {/* Stage */}
      <div style={{ padding: "0 10px", display: "flex", justifyContent: "center", ...getColCellHighlight("stage", activeSortKey, userHasSetSort) }}>
        <StageBadge
          stage={loc.stage}
          pillStyle={{ borderRadius: 6, border: "none", ...(density === "compact" ? { height: 20, fontSize: T.fsXxs } : {}) }}
        />
      </div>

      {/* M&A */}
      <div style={{ padding: "0 10px", textAlign: "center", color: "var(--v2-light)", fontSize: T.fsSm, ...getColCellHighlight("mAndA", activeSortKey, userHasSetSort) }}>
        {loc.mAndA ? getMAndATagName(loc.mAndA) : <span style={{ color: "var(--v2-light)" }}>—</span>}
      </div>

      <SpaceFitCell loc={loc} density={density} extraStyle={getColCellHighlight("space_fit_verdict", activeSortKey, userHasSetSort)} />
      <OfficeDensityCell loc={loc} extraStyle={getColCellHighlight("sfPerSeat", activeSortKey, userHasSetSort)} />

      {/* Sq ft
            VACATED-DETAIL-RETENTION (O-27): vacated/retired offices show their
            retained pre-exit sqft (displayInstalledSqft) in a muted style so
            it's clear the office is inactive — not added to usable totals. */}
      <div style={{ padding: "0 14px 0 6px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: isSqftRetained ? "var(--v2-light)" : "var(--v2-mid)", fontSize: T.fsBase, ...getColCellHighlight("sqft", activeSortKey, userHasSetSort) }}>
        {sqftStr || (isServicedNoArea(loc)
          ? <ServicedNaLabel />
          : <span style={{ color: "var(--v2-light)" }}>—</span>
        )}
      </div>

      {/* Seats
            VACATED-SEATS-DISPLAY-CONSISTENCY (O-24): vacated offices show their
            real pre-exit installed seat count (displayInstalledCapacity) in a
            muted style so it's clear they are inactive — they are never counted
            in the usable Total Seats KPI.  Non-vacated offices continue to read
            installedCapacity as before. */}
      <div style={{ padding: "0 14px 0 6px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: T.fsBase, ...getColCellHighlight("seats", activeSortKey, userHasSetSort) }}>
        {(() => {
          const displaySeats = loc.displayInstalledCapacity ?? seats;
          if (displaySeats == null) return <span style={{ color: "var(--v2-light)" }}>—</span>;
          const isVacated = loc.stage === "vacated";
          return (
            <span style={{ color: isVacated ? "var(--v2-light)" : "var(--v2-mid)" }}>
              {displaySeats.toLocaleString()}
            </span>
          );
        })()}
      </div>

      {/* People — amber dot appears when the row is recon-flagged or vacated-HC-flagged */}
      <div
        data-testid={`v2-people-col-${loc.id}`}
        style={{ padding: "0 14px 0 6px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--v2-mid)", fontSize: T.fsBase, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, ...getColCellHighlight("people", activeSortKey, userHasSetSort) }}
      >
        {people != null && people > 0
          ? people.toLocaleString()
          : <span style={{ color: "var(--v2-light)" }}>—</span>}
        {isReconFlagged && (
          <span
            aria-hidden="true"
            data-testid={`v2-recon-flag-${loc.id}`}
            title={reconTitle}
            style={{
              display: "inline-block",
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: T.ragAmber,
              flexShrink: 0,
            }}
          />
        )}
        {/* VACATED-OFFICE-ASSIGNED-HC-FLAG (O-28): amber dot when vacated/retired
            office still carries assigned headcount. De-duplicated — only one dot
            shown even if both flags apply (recon dot already present above). */}
        {isVacatedHcFlagged && !isReconFlagged && (
          <span
            ref={vacatedHcDotRef}
            {...vacatedHcIntent.triggerProps}
            tabIndex={0}
            style={{ cursor: "help" }}
          >
            <span
              aria-hidden="true"
              data-testid={`v2-vacated-hc-flag-${loc.id}`}
              style={{
                display: "inline-block",
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: T.ragAmber,
                flexShrink: 0,
              }}
            />
            <PortalDotTooltip show={vacatedHcTip} triggerRef={vacatedHcDotRef}>
              {`${Number(loc.workdayHc ?? 0).toLocaleString()} still assigned in Workday`}
            </PortalDotTooltip>
          </span>
        )}
      </div>

      {/* Peak — most-recent badge attendance peak (recentPeakBadges) */}
      <PeakCell loc={loc} extraStyle={getColCellHighlight("peak", activeSortKey, userHasSetSort)} />

      {/* Headroom — schedule-aware densification headroom via computeOfficeHorizonHeadroom.
          Null means no data on file; 0 means stage-gated (in-build / inactive / exiting). */}
      {(() => {
        const headroom = computeOfficeHorizonHeadroom(loc, TODAY);
        const headroomHighlight = getColCellHighlight("headroom", activeSortKey, userHasSetSort);
        if (headroom == null) {
          return (
            <div
              data-testid={`v2-headroom-${loc.id}`}
              title="Missing max density - edit this office to add it"
              style={{ padding: "0 14px 0 6px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--v2-light)", fontSize: T.fsBase, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, ...headroomHighlight }}
            >
              —
              {/* MAX-CAPACITY-BACKFILL-S1: flag the missing max_capacity data
                  gap rather than only showing a silent "—". */}
              <TriangleAlert
                data-testid={`v2-headroom-missing-flag-${loc.id}`}
                size={11}
                aria-hidden="true"
                style={{ color: "var(--v2-gold)", flexShrink: 0 }}
              />
            </div>
          );
        }
        if (headroom < 0) {
          return (
            <div
              data-testid={`v2-headroom-${loc.id}`}
              style={{ padding: "0 14px 0 6px", textAlign: "right", color: T.ragRed, fontSize: T.fsBase, fontVariantNumeric: "tabular-nums", ...headroomHighlight }}
            >
              {`\u2212${Math.abs(headroom).toLocaleString()}`}
            </div>
          );
        }
        if (headroom === 0) {
          return (
            <div
              data-testid={`v2-headroom-${loc.id}`}
              style={{ padding: "0 14px 0 6px", textAlign: "right", color: "var(--v2-mid)", fontSize: T.fsBase, fontVariantNumeric: "tabular-nums", ...headroomHighlight }}
            >
              0
            </div>
          );
        }
        return (
          <div
            data-testid={`v2-headroom-${loc.id}`}
            style={{ padding: "0 14px 0 6px", textAlign: "right", color: T.greenDeep, fontSize: T.fsBase, fontVariantNumeric: "tabular-nums", ...headroomHighlight }}
          >
            {`+${headroom.toLocaleString()}`}
          </div>
        );
      })()}

    </div>
  );
}


// ── KPI tile ──────────────────────────────────────────────────────────────────

// KpiTile is now imported from src/components/KpiTile.jsx


// ── Detail drawer ─────────────────────────────────────────────────────────────

function DetailDrawer({ loc, onClose, onOpenDetail }) {
  if (!loc) return null;

  const sqftVal = getDisplayedSqft(loc, null);
  const sqftStr = sqftVal != null ? fmtSqft(sqftVal) : (isServicedNoArea(loc) ? "n/a · serviced" : "—");
  const seats   = loc.installedCapacity != null ? Number(loc.installedCapacity).toLocaleString() : "—";
  const hc      = loc.latestHeadcount;
  const people  = hc?.totalCount != null && Number(hc.totalCount) > 0
    ? Number(hc.totalCount).toLocaleString() : "—";

  const seatNum = loc.installedCapacity != null ? Number(loc.installedCapacity) : null;
  const hcNum   = hc?.totalCount != null ? Number(hc.totalCount) : null;
  const assignedStr = seatNum && hcNum && seatNum > 0
    ? Math.round((hcNum / seatNum) * 100) + "%" : "—";

  const utilStr = loc.avgUtilPct != null ? Math.round(loc.avgUtilPct * 100) + "%" : "—";

  const leaseTypes = getActiveLeaseTypes(loc);
  const leaseLabel = leaseTypes.map((t) => LEASE_DISPLAY[t] ?? t).join(", ") || "—";
  const stageInfo  = STAGES[loc.stage];

  return (
    <div
      data-testid="v2-detail-drawer"
      style={{
        height: "100%",
        background: "var(--v2-card)",
        display: "flex", flexDirection: "column",
        boxShadow: "-4px 0 24px rgba(16,36,58,.12)",
      }}
    >
      {/* Header */}
      <div style={{
        padding: "20px 20px 16px",
        borderBottom: "1px solid var(--v2-line)",
        background: "var(--v2-tint)",
      }}>
        <div>
          <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: "var(--v2-light)",
                        textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>
            {loc.region || "—"}
          </div>
        </div>
        <div style={{ fontSize: T.fsLg5, fontWeight: T.fwBold, color: "var(--v2-dark)", marginBottom: 4 }}>
          {loc.market}
        </div>
        {loc.locationAlias && (
          <div style={{ fontSize: T.fsSm5, color: "var(--v2-mid)", marginBottom: 8 }}>
            {loc.locationAlias}
          </div>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: T.fsXs }}>
          {stageInfo && (
            <span style={{
              display: "inline-flex", alignItems: "center", background: stageInfo.bg, color: stageInfo.color,
              borderRadius: 6, padding: "2px 10px", fontWeight: T.fwSemibold,
            }}>{stageInfo.label}</span>
          )}
          {loc.isHub && (
            <span style={{
              display: "inline-flex", alignItems: "center", background: T.hubChipBg, color: T.hubChipText,
              borderRadius: 6, padding: "2px 10px", fontWeight: T.fwSemibold,
            }}>◆ Hub</span>
          )}
          {leaseLabel !== "—" && (
            <span style={{
              display: "inline-flex", alignItems: "center", background: "var(--v2-tint)", color: "var(--v2-mid)",
              borderRadius: 6, padding: "2px 10px", fontWeight: T.fwSemibold,
            }}>{leaseLabel}</span>
          )}
          {loc.mAndA && (
            <span style={{
              display: "inline-flex", alignItems: "center", background: "var(--v2-blue-tint)", color: "var(--v2-accent)",
              borderRadius: 6, padding: "2px 10px", fontWeight: T.fwSemibold,
            }}>{getMAndATagName(loc.mAndA)}</span>
          )}
        </div>
      </div>

      {/* Key fields */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
        {[
          { label: "Sq ft",           value: sqftStr  },
          { label: "Seats",           value: seats    },
          { label: "People",          value: people   },
          { label: "Assigned %",      value: assignedStr },
          { label: "Utilization",     value: utilStr  },
        ].map(({ label, value, testId }) => (
          <div key={label} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "10px 0", borderBottom: "1px solid var(--v2-hair)",
          }}>
            <span style={{ fontSize: T.fsSm5, color: "var(--v2-mid)" }}>{label}</span>
            <span
              data-testid={testId}
              style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: "var(--v2-dark)" }}
            >
              {value}
            </span>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ padding: "14px 20px", borderTop: "1px solid var(--v2-line)" }}>
        <button
          type="button"
          data-testid={`v2-drawer-open-${loc.id}`}
          onClick={() => onOpenDetail(loc.id)}
          style={{
            display: "block", width: "100%", textAlign: "center",
            background: "var(--v2-accent)", color: T.white,
            border: 0, borderRadius: 9, padding: "10px 14px",
            fontSize: T.fsBase, fontWeight: T.fwBold, cursor: "pointer",
          }}
        >
          Open location →
        </button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function OfficesPage({
  refreshKey = 0,
  onAddLocation,
  currentRole = null,
  userKey = null,
  setSearch = () => {},
  onNavListChange = null,
  planData = [],
}) {
  // Keep the action locked unless a known write-capable role and callback are
  // both present. This also keeps legacy callers without role data read-only.
  const canAddLocation = typeof onAddLocation === "function"
    && (currentRole === "admin" || currentRole === "editor");
  const { flags, flagsReady, nav_config, nav_order } = useFeatureFlagContext();
  const marketsEnabled = flagsReady && governedNavEnabled(flags, nav_config, "plan", nav_order);
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  // OFFICES-URL-PARAMS: Read the router's URL once at component construction time
  // (synchronous, no hooks) so they're available before any effects fire.
  // We use a ref instead of useState to avoid an extra render cycle.
  const urlBagRef = useRef(undefined);
  if (urlBagRef.current === undefined) {
    urlBagRef.current = decodeOfficesParams(routerLocation.search);
  }
  const initialUrlBag = urlBagRef.current;

  const [rawLocations, setLocations] = useState([]);
  const fitPolicyState = useSpacePolicy();
  const locations = useMemo(() => rawLocations.map(loc => ({ ...loc,
    seatFitToday: { state: fitPolicyState.status === "ok" ? "ok" : fitPolicyState.status, policy: fitPolicyState.baseline, bufferPct: fitPolicyState.baseline?.hcBufferPct,
      error: fitPolicyState.error } })), [rawLocations, fitPolicyState]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  const [reportsLoading,     setReportsLoading]     = useState(true);
  const [unassignedHeadcount, setUnassignedHeadcount] = useState(null);
  const [headcountReportingMonth, setHeadcountReportingMonth] = useState(null);

  // Single strip animation key for all 5 KPI tiles — fired once both
  // data sources (locations, reports) have completed their fetch.
  // Filter changes never touch this key, so animation only replays on refresh.
  const [locAnimKey,      setLocAnimKey]      = useState(0);
  const kpiReadyCountRef = useRef(0); // counts fetch completions per refresh cycle

  const [viewState, setViewState, clearViewState] = useUiPrefs(
    UI_PREFS_OFFICES_V1,
    DEFAULT_V2_VIEW_STATE,
    migrateOfficePrefs,
  );
  const { selections, viewMode, density } = viewState;
  const sortKey = viewState.sortKey ?? "location";
  const sortDir = viewState.sortDir ?? "asc";
  const sortKey2 = viewState.sortKey2 ?? null;
  const sortDir2 = viewState.sortDir2 ?? "asc";
  // search is persisted in viewState; fall back to "" for blobs written before
  // this field was added (they won't have the key).
  const search = viewState.search ?? "";

  const setSelections = useCallback(
    (sel) => setViewState((s) => ({ ...s, selections: sel })),
    [setViewState],
  );

  // On mount, sync the restored search value (empty or not) back to the App
  // shell so that deriveNavLocations sees the same predicate as the visible list.
  useEffect(() => {
    setSearch(viewState.search ?? "");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // OFFICES-URL-PARAMS: Apply URL param bag to viewState before first paint.
  // useLayoutEffect is synchronous so the user never sees a flash of the
  // sessionStorage state when URL params are present. Runs exactly once.
  const urlHydratedRef = useRef(false);
  useLayoutEffect(() => {
    if (urlHydratedRef.current || !initialUrlBag) return;
    urlHydratedRef.current = true;
    setViewState((s) => applyUrlBagToViewState(s, initialUrlBag));
    // Sync search override to the app shell immediately.
    if (initialUrlBag.q !== undefined) setSearch(initialUrlBag.q);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // OFFICES-URL-PARAMS: Canonicalize ?view= on mount.
  // ?view=list (URL default) and ?view=<unrecognized> are stripped via
  // the router so the URL stays clean without losing hash/history state.
  useLayoutEffect(() => {
    const raw = new URLSearchParams(routerLocation.search).get("view");
    if (!raw) return;
    if (raw === VIEW_TILES || raw === VIEW_MAP) return;
    const p = new URLSearchParams(routerLocation.search);
    p.delete("view");
    const qs = p.toString();
    navigate({ pathname: routerLocation.pathname, search: qs ? `?${qs}` : "", hash: routerLocation.hash },
      { replace: true, state: routerLocation.state });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // OFFICES-URL-PARAMS: Track whether we should suppress the next URL write
  // (used by handleClear to navigate to bare /offices without the effect
  //  immediately re-writing the default-state params back).
  const skipNextUrlWriteRef = useRef(false);

  // OFFICES-URL-PARAMS: Track whether the first render has passed.
  // On a bare /offices load (no URL params), we skip the initial URL write so
  // we don't pollute the URL on first visit. After that, every viewState or
  // drawerLoc change writes the URL.
  const firstRenderDoneRef = useRef(false);

  const handleSearchChange = useCallback(
    (val) => {
      setViewState((s) => ({ ...s, search: val }));
      setSearch(val);
    },
    [setViewState, setSearch],
  );

  // Task #9085: the record drawer is route-owned — /places/offices/{id}?tab=.
  // Selection, tab and list context all live in the URL; no local mirror.
  const { locationId: routeLocationId = null } = useParams();
  const routeTab = normalizeOfficeDrawerTab(new URLSearchParams(routerLocation.search).get("tab"));
  const drawerReturn = readRecordReturn(routerLocation.state);
  // List context = current query minus drawer-owned params.
  const listSearchFor = useCallback((search) => {
    const params = new URLSearchParams(search);
    params.delete("tab");
    params.delete("peek");
    const q = params.toString();
    return q ? `?${q}` : "";
  }, []);
  const officeDrawerUrl = useCallback((id, tab = null) => {
    const params = new URLSearchParams(listSearchFor(routerLocation.search));
    if (viewMode !== VIEW_LIST) params.set("view", viewMode);
    return `${buildOfficeDrawerUrl(id, tab, params)}${routerLocation.hash}`;
  }, [routerLocation.search, routerLocation.hash, listSearchFor, viewMode]);
  const openOfficeDrawer = useCallback((id, { tab = null, replace = false, state } = {}) => {
    navigate(officeDrawerUrl(id, tab), { replace, state });
  }, [navigate, officeDrawerUrl]);
  const closeOfficeDrawer = useCallback(() => {
    const returnPath = routerLocation.state?.returnPath;
    if (/^\/reports\/space-fit(?:[?#]|$)/.test(returnPath ?? "")) {
      navigate(returnPath);
      return;
    }
    navigate(`/places/offices${listSearchFor(routerLocation.search)}${routerLocation.hash}`);
  }, [navigate, routerLocation.search, routerLocation.hash, routerLocation.state, listSearchFor]);

  // OFFICES-RECONCILIATION-ALERT: filter-chip toggle that restricts the list
  // to reconciliation-flagged offices only. Cleared by "Clear all" and by
  // dismissing the alert or clicking the action button a second time.
  const [reconOnly,      setReconOnly]      = useState(false);
  // VACATED-OFFICE-ASSIGNED-HC-FLAG (O-28): filter-chip toggle that restricts
  // the list to vacated/retired offices with assigned HC > 0.
  const [vacatedHcOnly,  setVacatedHcOnly]  = useState(false);

  const headerScrollRef = useRef(null);
  const bodyScrollRef   = useRef(null);
  useCollectionCellTitles(bodyScrollRef, '[data-list-row] > div');
  const geometryRootRef = useRef(null);
  useOfficeListGeometry(geometryRootRef);
  const [overflowElement, setOverflowElement] = useState(null);
  const setGeometryElement = useCallback(element => {
    geometryRootRef.current = element;
    setOverflowElement(element);
  }, []);
  const listOverflow = useListOverflow({ element: overflowElement, scrollRef: bodyScrollRef, grid: true, invalidate: viewMode });
  const sentinelRef     = useRef(null);
  const spaceFitBtnRef  = useRef(null);
  const hubBtnRef       = useRef(null);
  const stageBtnRef     = useRef(null);
  const mAndABtnRef     = useRef(null);
  const peopleBtnRef    = useRef(null);
  const headroomBtnRef  = useRef(null);
  const assignedPctBtnRef = useRef(null);

  const isStuck = useListHeaderStuck({ sentinelRef, headerScrollRef, enabled: viewMode === VIEW_LIST });
  const spaceFitIntent = useHoverIntent({ interactive: true });
  const spaceFitShow = spaceFitIntent.open;
  const [activeTextTooltip, setActiveTextTooltip] = useState(null);
  const textTooltipKey = useRef(null);
  const textIntent = useHoverIntent({
    onOpen: () => setActiveTextTooltip(textTooltipKey.current),
    onClose: () => setActiveTextTooltip(null),
  });

  // Load config-driven benchmark bands for the Assign % bar on mount.
  // The setter triggers a re-render so classify() reads the freshly populated
  // module-level cache. Only the setter is needed here; the value lives in the
  // benchmarkBandsService module cache and is consumed directly by classify().
  const [, setAssignBands] = useState([]);
  useEffect(() => {
    getBands().then(setAssignBands);
  }, []);

  const handleBodyScroll = useCallback(() => {
    if (headerScrollRef.current && bodyScrollRef.current) {
      headerScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    }
  }, []);

  // ── Data fetches ───────────────────────────────────────────────────────────

  // Reset the per-refresh counter whenever a new fetch cycle begins.
  // Runs before the fetch effects (React executes effects in source order).
  useEffect(() => {
    kpiReadyCountRef.current = 0;
  }, [refreshKey]);

  useRecordPhotoRefresh(() => getLocations().then(list => setLocations(previous => {
    const covers = new Map(list.map(row => [row.id, row]));
    // Do not clobber unsaved inline fields or collection/scroll selection.
    return previous.map(row => {
      const next = covers.get(row.id);
      return next ? { ...row, imageUrl: next.imageUrl, effectiveCoverUrl: next.effectiveCoverUrl } : row;
    });
  })).catch(setError));
  useEffect(() => subscribeAttendanceSourceChange(() => {
    setLocations([]);
    setLoading(true);
    setLocAnimKey((key) => key + 1);
    getLocations()
      .then((list) => { setLocations(list); setError(null); })
      .catch((err) => {
        log.error("OfficesPage: attendance source refresh failed", { msg: err?.message });
        setError(err);
      })
      .finally(() => setLoading(false));
  }), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getLocations()
      .then((list) => { if (!cancelled) { setLocations(list); setError(null); } })
      .catch((err) => {
        if (!cancelled) {
          log.error("OfficesPage: getLocations failed", { msg: err?.message });
          setError(err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          kpiReadyCountRef.current += 1;
          if (kpiReadyCountRef.current >= 2) setLocAnimKey((k) => k + 1);
        }
      });
    return () => { cancelled = true; };
  }, [refreshKey]);

  useEffect(() => {
    let cancelled = false;
    setReportsLoading(true);
    getHeadcountTrendReport()
      .then((report) => {
        if (!cancelled) {
          const aggregate = Array.isArray(report?.aggregate) ? report.aggregate : [];
          const latest = aggregate[aggregate.length - 1];
          setUnassignedHeadcount(latest?.unassigned ?? null);
          setHeadcountReportingMonth(latest?.snapshotDate ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUnassignedHeadcount(null);
          setHeadcountReportingMonth(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setReportsLoading(false);
          kpiReadyCountRef.current += 1;
          if (kpiReadyCountRef.current >= 2) setLocAnimKey((k) => k + 1);
        }
      });
    return () => { cancelled = true; };
  }, [refreshKey]);

  // ── Derived ────────────────────────────────────────────────────────────────

  // OFFICES-RECONCILIATION-ALERT: compute the flagged set over the FULL
  // (pre-filter) locations list so the chip count stays stable as the user
  // scrolls / changes filters, and so reconOnly can reveal offices that would
  // otherwise be hidden by the current stage/region filter.
  const reconFlaggedIds = useMemo(
    () => computeReconFlaggedIds(locations),
    [locations],
  );

  // VACATED-OFFICE-ASSIGNED-HC-FLAG (O-28): compute over the FULL list for the
  // same reason — so the chip count is stable and vacatedHcOnly can surface
  // vacated/retired offices hidden by the current stage filter.
  const vacatedHcFlaggedIds = useMemo(
    () => computeVacatedHcFlaggedIds(locations),
    [locations],
  );

  const searchMatches = useMemo(() => search.trim()
    ? new Map(locations.map((loc) => [loc.id, matchOfficeIdentity(loc, search)]))
    : new Map(), [locations, search]);
  const filtered = useMemo(() => {
    let list;
    if (vacatedHcOnly) {
      // VACATED-OFFICE-ASSIGNED-HC-FLAG (O-28): bypass the stage filter so
      // vacated/retired offices hidden by the default scope (operational +
      // dormant) become visible when the user clicks "Filter to flagged".
      // Other filter dimensions (region, lease, mAndA, space_fit) still apply.
      // reconOnly is mutually exclusive with vacatedHcOnly — the latter wins.
      const selectionsNoStage = { ...selections, stage: allOptionValues(FILTER_DIMENSIONS[0]) };
      list = applySearchFilter(applyV2Filters(locations, selectionsNoStage, true), search)
        .filter((loc) => vacatedHcFlaggedIds.has(loc.id));
    } else {
      list = applySearchFilter(applyV2Filters(locations, selections, true), search);
      if (reconOnly) list = list.filter((loc) => reconFlaggedIds.has(loc.id));
    }
    const sorted = sortRows(list, sortKey, sortDir, sortKey2, sortDir2);
    return viewMode === VIEW_LIST ? rankIdentityMatches(sorted, searchMatches) : sorted;
  }, [locations, selections, search, searchMatches, viewMode, reconOnly, reconFlaggedIds, vacatedHcOnly, vacatedHcFlaggedIds, sortKey, sortDir, sortKey2, sortDir2]);
  const hiddenSearchGroups = useMemo(() => restrictedFilterGroups(
    FILTER_DIMENSIONS.filter((dim) => !vacatedHcOnly || dim.id !== "stage"),
    selections,
    true,
  ), [selections, vacatedHcOnly]);
  const countedFilterDimensions = useMemo(() => withFacetCounts(
    FILTER_DIMENSIONS, selections, applySearchFilter(locations, search),
    (rows, selected, countGroup) => applyV2Filters(rows, vacatedHcOnly
      ? { ...selected, stage: allOptionValues(FILTER_DIMENSIONS[0]) } : selected, true, countGroup)
      .filter(loc => vacatedHcOnly ? vacatedHcFlaggedIds.has(loc.id) : !reconOnly || reconFlaggedIds.has(loc.id)),
  ), [selections, locations, search, vacatedHcOnly, reconOnly, vacatedHcFlaggedIds, reconFlaggedIds]);
  const revealedSelections = useMemo(() => relaxFilterGroups(selections, hiddenSearchGroups, true), [selections, hiddenSearchGroups]);
  const hiddenSearchHint = useMemo(() => {
    if (viewMode !== VIEW_LIST || !search.trim() || !hiddenSearchGroups.length) return null;
    const comparisonSelections = vacatedHcOnly ? { ...revealedSelections, stage: allOptionValues(FILTER_DIMENSIONS[0]) } : revealedSelections;
    const candidates = applySearchFilter(applyV2Filters(locations, comparisonSelections, true), search)
      .filter((loc) => vacatedHcOnly ? vacatedHcFlaggedIds.has(loc.id) : !reconOnly || reconFlaggedIds.has(loc.id));
    return deriveFilterHiddenSearch({ query: search, groups: hiddenSearchGroups, selections, candidates, visible: filtered, checked: true });
  }, [viewMode, search, hiddenSearchGroups, revealedSelections, vacatedHcOnly, reconOnly, vacatedHcFlaggedIds, reconFlaggedIds, locations, selections, filtered]);
  // NAV-PREVNEXT-RESPECT-FILTER-SCOPE: Push current filtered+sorted ID sequence
  // to AppShell so Prev/Next on the location detail page respects active filters.
  // Runs whenever `filtered` changes (filter/sort/search change) so the shell
  // always has an up-to-date ordered list.
  useEffect(() => {
    if (typeof onNavListChange === "function") {
      onNavListChange(filtered.map((l) => l.id));
    }
  }, [filtered, onNavListChange]);

  // Filter-scoped stats — used by the alert chip messages and sqft tile (filter-scoped).
  const stats = useMemo(() => computePortfolioStats(filtered), [filtered]);
  const filteredMarketCount = useMemo(() => getDistinctMarketCount(filtered.filter((loc) => loc.stage !== "draft")), [filtered]);

  // CAPACITY-DEFINITION-FIX: portfolio-wide usable-capacity stats — computed from the
  // FULL (unfiltered) locations list, restricted to usable stages (operational + dormant).
  // These drive the 4 portfolio KPI tiles. Build and vacated offices are intentionally
  // excluded because their seats are not live occupancy.
  const portfolioUsable = useMemo(
    () => locations.filter((loc) => USABLE_CAPACITY_STAGES.includes(loc.stage)),
    [locations],
  );
  const portfolioStats = useMemo(() => computePortfolioStats(portfolioUsable), [portfolioUsable]);
  const portfolioHeadcount = useMemo(
    () => portfolioUsable.reduce((s, loc) => s + (Number(loc.latestHeadcount?.totalCount) || 0), 0),
    [portfolioUsable],
  );
  const portfolioAssignPct = portfolioStats.totalCapacity > 0
    ? Math.round(portfolioHeadcount / portfolioStats.totalCapacity * 100)
    : null;

  // OFFICES-RECONCILIATION-ALERT: count flagged offices in the current
  // filtered view for the chip label. When reconOnly is active this equals
  // filtered.length; when inactive it counts the subset of flagged rows.
  const reconFlagCount = useMemo(
    () => filtered.filter((loc) => reconFlaggedIds.has(loc.id)).length,
    [filtered, reconFlaggedIds],
  );

  // VACATED-OFFICE-ASSIGNED-HC-FLAG (O-28): count from the FULL (pre-filter)
  // locations list so the chip label is stable even when vacated/retired offices
  // are hidden by the default stage filter (operational + dormant only).
  // Counting from `filtered` would return 0 whenever the default view hides
  // vacated offices, making the AlertChip never appear.
  const vacatedHcFlagCount = useMemo(
    () => locations.filter((loc) => vacatedHcFlaggedIds.has(loc.id)).length,
    [locations, vacatedHcFlaggedIds],
  );

  // OFFICES-KPI-HEADCOUNT-FIX: sum headcount directly from the filtered
  // locations list (mirrors the PEOPLE column in GridRow) so offices that
  // are absent from the capacity report (e.g. null-sqft offices filtered by
  // the server-side market-active guard) still contribute to the KPI total.
  const headcountTotal = useMemo(
    () => filtered.reduce((s, loc) => s + (loc.stage === "draft" ? 0 : (Number(loc.latestHeadcount?.totalCount) || 0)), 0),
    [filtered],
  );
  const assignmentCoverage = useMemo(() => knownTotal(filtered.filter(loc => loc.stage !== "draft"),
    loc => loc.latestHeadcount?.totalCount), [filtered]);

  const totalSqftStr = stats.knownSqftCount
    ? (stats.totalSqft >= 1_000_000
        ? (stats.totalSqft / 1_000_000).toFixed(1) + "M"
        : Math.round(stats.totalSqft).toLocaleString("en-US"))
    : "—";

  // KPI-SCOPE-INDICATOR (G-22): headcountStr now tracks the filter-scoped
  // headcountTotal so the Assigned headcount tile reacts to active filters.
  const headcountStr = loading ? "…"
    : headcountTotal > 0 ? headcountTotal.toLocaleString() : "—";

  // KPI-SCOPE-INDICATOR (G-22): true when a filter or search is actively
  // narrowing the view from the default scope (Operational + Dormant).
  const isFiltered =
    search.trim() !== "" ||
    !selectionsEqual(selections, DEFAULT_SCOPE_SELECTIONS);

  // OFFICES-URL-PARAMS: Sync viewState + drawerLoc to URL on every change
  // using replace:true so the back button is not spammed.
  //
  // Skip the first render when there were no initial URL params (bare /offices
  // load) — we don't want to pollute a clean URL just because the user arrived.
  // After that first render, every state change writes the URL.
  //
  // encodeOfficesParams returns "" when the state is fully default (no layout
  // override, no filters, no sort, etc.) — in that case we navigate to the bare
  // pathname so the URL stays clean.
  const drawerLocId = null; // drawer selection lives in the path (Task #9085)
  useEffect(() => {
    if (!firstRenderDoneRef.current) {
      firstRenderDoneRef.current = true;
      // If there were no URL params on load, don't start writing params yet.
      if (!initialUrlBag) return;
    }
    if (skipNextUrlWriteRef.current) {
      skipNextUrlWriteRef.current = false;
      return;
    }
    const params = mergeOfficesSearch(routerLocation.search, encodeOfficesParams(viewState, drawerLocId));
    // Drawer path owns ?tab; preserve it through list-param rewrites.
    const nextSearch = params ? `?${params}` : "";
    if (nextSearch === routerLocation.search) return;
    navigate({ pathname: routerLocation.pathname, search: nextSearch, hash: routerLocation.hash },
      { replace: true, state: routerLocation.state });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewState, drawerLocId, navigate]);

  // Legacy ?peek={id} bookmarks resolve to the drawer path (one replace),
  // keeping the remaining collection context.
  useEffect(() => {
    const peek = new URLSearchParams(routerLocation.search).get("peek");
    if (!peek || routeLocationId) return;
    openOfficeDrawer(peek, { replace: true, state: routerLocation.state });
  }, [routerLocation.search, routeLocationId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Legacy lease-drawer preload state ({ preloadLoc }) also opens the drawer.
  useEffect(() => {
    const preloadLoc = routerLocation.state?.preloadLoc;
    if (!preloadLoc?.id || routeLocationId) return;
    openOfficeDrawer(preloadLoc.id, { replace: true });
  }, [routerLocation.state?.preloadLoc]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ───────────────────────────────────────────────────────────────

  function handleViewMode(mode) {
    setViewState((s) => ({ ...s, viewMode: mode }));
  }

  function handleRowClick(loc) {
    if (!loc?.id) return;
    openOfficeDrawer(loc.id, { replace: Boolean(routeLocationId) });
  }

  function handleOpenDetail(locId, tab) {
    // Explicit full-page actions must not recurse through ordinary selection.
    navigate(buildOfficeFullUrl(locId, tab), {
      state: {
        ...routerLocation.state,
        returnPath: `${routerLocation.pathname}${routerLocation.search}${routerLocation.hash}`,
        orderedIds: filtered.map((l) => l.id),
        initTab: tab ?? "building",
      },
    });
  }

  const handleSort = useCallback(
    (key) => {
      setViewState((s) => {
        const prevKey = s.sortKey ?? "location";
        const prevDir = s.sortDir ?? "asc";
        if (prevKey === key) {
          return { ...s, sortDir: prevDir === "asc" ? "desc" : "asc", userHasSetSort: true };
        }
        // Numeric columns default to descending on first click (highest value on top).
        // Text/categorical columns default to ascending (A–Z).
        const firstDir = NUMERIC_SORT_KEYS.has(key) ? "desc" : "asc";
        return { ...s, sortKey: key, sortDir: firstDir, userHasSetSort: true };
      });
    },
    [setViewState],
  );

  const handleSortChange = useCallback(
    ({ primary, secondary }) => {
      setViewState((s) => ({
        ...s,
        sortKey: primary.key,
        sortDir: primary.dir,
        sortKey2: secondary.key,
        sortDir2: secondary.dir,
        userHasSetSort: true,
      }));
    },
    [setViewState],
  );

  // isNonDefaultView is true when the current state differs from the first-load
  // scope default (DEFAULT_SCOPE_SELECTIONS — Operational + Dormant only, NOT
  // the "Active" saved-view preset which also includes Build), when there is a
  // search term, OR when the user has changed the column sort from the default.
  // DEFAULT_SCOPE_SELECTIONS has non-empty stage[], so we cannot use a simple
  // any-non-empty check — that would leave Clear stuck showing in the default state.
  // reconOnly and vacatedHcOnly are also considered non-default so Clear appears.
  const isNonDefaultView =
    !selectionsEqual(selections, DEFAULT_SCOPE_SELECTIONS) ||
    search !== "" ||
    (viewMode === VIEW_LIST
      ? sortKey !== DEFAULT_V2_VIEW_STATE.sortKey || sortDir !== DEFAULT_V2_VIEW_STATE.sortDir || sortKey2 != null
      : viewState.userHasSetSort === true) ||
    reconOnly ||
    vacatedHcOnly;

  // List Clear owns filters/sort only; search has its own ×. Keep the full
  // reset contract for Tiles/Map and the separate empty-state action.
  const showToolbarClear = viewMode === VIEW_LIST
    ? !selectionsEqual(selections, DEFAULT_SCOPE_SELECTIONS) ||
      sortKey !== DEFAULT_V2_VIEW_STATE.sortKey ||
      sortDir !== DEFAULT_V2_VIEW_STATE.sortDir ||
      sortKey2 != null || reconOnly || vacatedHcOnly
    : isNonDefaultView;

  // Map and sidebar render only coordinate-eligible offices. Reuse their
  // actual predicate for alerts and pager membership, retaining List sort order.
  const mappedLocations = useMemo(() => filtered.filter(hasCoords), [filtered]);
  const mapFilteredIds = useMemo(() => mappedLocations.map((loc) => loc.id), [mappedLocations]);
  const mapHiddenCount = viewMode === VIEW_MAP ? filtered.length - mappedLocations.length : 0;

  // ALERT-CHIP-AND-TWO-CLUSTER-TOOLBAR: single message set feeding the
  // leading-toolbar AlertChip, scoped per view. Map shows ONLY the
  // map-scoped no-coordinates alert; List/Tiles show the data-completeness
  // alerts. All "N need attention / N excluded" notices route through this
  // chip+panel — no bespoke text anywhere else on the page.
  const handleReconFilterToggle = useCallback(
    () => setReconOnly((v) => !v),
    [],
  );

  // VACATED-OFFICE-ASSIGNED-HC-FLAG (O-28): toggle the vacated-HC-only filter.
  const handleVacatedHcFilterToggle = useCallback(
    () => setVacatedHcOnly((v) => !v),
    [],
  );

  const officesAlertMessages = useMemo(() => {
    if (viewMode === VIEW_MAP) {
      if (mapHiddenCount === 0) return [];
      return [{
        id:       "map-hidden-offices",
        severity: "warn",
        text:     `${mapHiddenCount} ${mapHiddenCount === 1 ? "office" : "offices"} not shown - no coordinates on file.`,
      }];
    }
    const msgs = [];
    // OFFICES-RECONCILIATION-ALERT: reconciliation signal leads the list so
    // operators notice data-integrity issues before cosmetic completeness gaps.
    if (reconFlagCount > 0) {
      msgs.push({
        id:          "hc-reconciliation",
        severity:    "warn",
        text:        `${reconFlagCount} ${reconFlagCount === 1 ? "office" : "offices"} flagged - headcount reconciliation`,
        actionLabel: reconOnly ? "Clear filter" : "Filter to flagged",
        onAction:    handleReconFilterToggle,
      });
    }
    // VACATED-OFFICE-ASSIGNED-HC-FLAG (O-28): separate chip for vacated/retired
    // offices that still carry assigned headcount — independent from the recon alert.
    if (vacatedHcFlagCount > 0) {
      msgs.push({
        id:          "vacated-hc-assigned",
        severity:    "warn",
        text:        `${vacatedHcFlagCount} vacated ${vacatedHcFlagCount === 1 ? "office" : "offices"} with assigned headcount — reassign in Workday`,
        actionLabel: vacatedHcOnly ? "Clear filter" : "Filter to flagged",
        onAction:    handleVacatedHcFilterToggle,
      });
    }
    if (stats.missingMaxCapacity > 0) {
      msgs.push({
        id:       "missing-max-capacity",
        severity: "warn",
        text:     `${stats.missingMaxCapacity} of ${filtered.length} offices missing max density - add via office edit to unlock Expansion.`,
      });
    }
    if (stats.missingInstalledCapacity > 0) {
      msgs.push({
        id:       "missing-installed-capacity",
        severity: "warn",
        text:     `${stats.missingInstalledCapacity} of ${filtered.length} offices missing installed capacity - add via office edit for accurate seat counts.`,
      });
    }
    return msgs;
  }, [viewMode, mapHiddenCount, reconFlagCount, reconOnly, handleReconFilterToggle, vacatedHcFlagCount, vacatedHcOnly, handleVacatedHcFilterToggle, stats.missingMaxCapacity, stats.missingInstalledCapacity, filtered.length]);

  // ── Keyboard navigation through filtered rows while drawer is open ──────────

  // Arrow-key paging is owned by the drawer's PrevNextPager (route replace).
  const drawerLoc = routeLocationId ? { id: routeLocationId } : null;
  const filteredIds = useMemo(() => filtered.map((l) => l.id), [filtered]);

  useEffect(() => {
    if (drawerLoc == null) return;
    const row = document.querySelector(`[data-testid="v2-loc-row-${drawerLoc.id}"]`);
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [routeLocationId]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleListToolbarClear() {
    setViewState((state) => ({
      ...state,
      selections: normalizeCheckedSelections(FILTER_DIMENSIONS, DEFAULT_SCOPE_SELECTIONS, DEFAULT_SCOPE_SELECTIONS),
      sortKey: DEFAULT_V2_VIEW_STATE.sortKey,
      sortDir: DEFAULT_V2_VIEW_STATE.sortDir,
      sortKey2: DEFAULT_V2_VIEW_STATE.sortKey2,
      sortDir2: DEFAULT_V2_VIEW_STATE.sortDir2,
      userHasSetSort: false,
    }));
    setReconOnly(false);
    setVacatedHcOnly(false);
  }

  function handleClear() {
    clearViewState();
    setSearch("");
    setReconOnly(false);
    setVacatedHcOnly(false);
    // OFFICES-URL-PARAMS: strip filter/sort/search params but preserve the
    // current viewMode so the user stays on their current view (List/Tiles/Map).
    // skipNextUrlWriteRef prevents the URL-sync effect from immediately
    // re-writing the default-state params after the clearViewState() re-render.
    skipNextUrlWriteRef.current = true;
    if (viewMode && viewMode !== VIEW_LIST) {
      const params = mergeOfficesSearch(routerLocation.search, `view=${viewMode}`);
      navigate({ pathname: routerLocation.pathname, search: `?${params}`, hash: routerLocation.hash },
        { replace: true, state: routerLocation.state });
    } else {
      const params = mergeOfficesSearch(routerLocation.search, "");
      navigate({ pathname: routerLocation.pathname, search: params ? `?${params}` : "", hash: routerLocation.hash },
        { replace: true, state: routerLocation.state });
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div data-testid="locations-list-v2">
      <PageHeaderStack>
      {/* KPI tiles
          KPI-SCOPE-INDICATOR (G-22): first 4 tiles are "responsive" — they
          reflect the currently-filtered view and show a funnel icon. The last
          two tiles are "portfolio" — they always reflect the full usable set
          regardless of active filters, and show "portfolio" as their sub-label.
          Responsive tiles turn blue (funnel + text) when isFiltered is true. */}
      <KpiBand testId="v2-kpi-tiles">
        <KpiTile
          testId="v2-tile-locations"
          height={KPI_TILE_ROW_HEIGHT}
          label="Offices"
          tooltip={<KpiPopover period="Today" source="Ripple portfolio">
            Operational + dormant offices matching the current filters.
          </KpiPopover>}
          tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
          tooltipPlacement="viewport-clamped"
          descriptor={marketsEnabled ? `across ${filteredMarketCount} markets` : "in this view"}
          value={loading ? "…" : stats.officeCount}
          rawValue={loading ? undefined : stats.officeCount}
          formatter={(n) => fmtInt(n)}
          animationKey={locAnimKey}
          tileIndex={0}
          color="var(--v2-accent)"
          scope="responsive"
          isFiltered={isFiltered}
        />
        <KpiTile
          testId="v2-tile-sqft"
          height={KPI_TILE_ROW_HEIGHT}
          label="Total sq ft"
          tooltip={<KpiPopover period="Today" source="Spacebase">
            Qualifying leases across the offices in view use current gross area, or valid unit-normalized parent lease size when current area is unavailable.
            Outbound subleases are subtracted. Unresolved leases are omitted, so a partly known office can understate its footprint.
            Exited space excluded. Offices with unknown footage are not included in the sum.
            {" "}{filtered.filter((office) => office.stage !== "draft" && getDisplayedSqft(office, null) == null).length} offices have unknown footage.
          </KpiPopover>}
          tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
          tooltipPlacement="viewport-clamped"
          descriptor="operational footprint"
          value={loading ? "…" : totalSqftStr}
          rawValue={loading ? undefined : stats.totalSqft}
          formatter={(n) =>
            n >= 1_000_000
              ? (n / 1_000_000).toFixed(1) + "M"
              : n >= 0 && stats.knownSqftCount > 0
              ? Math.round(n).toLocaleString("en-US")
              : "—"
          }
          animationKey={locAnimKey}
          tileIndex={1}
          color="var(--v2-green)"
          scope="responsive"
          isFiltered={isFiltered}
        />
        {/* KPI-SCOPE-INDICATOR (G-22): seats and headcount are now filter-scoped
            so they react to the active filter/search, matching Offices and Sqft. */}
        <KpiTile
          testId="v2-tile-seats"
          height={KPI_TILE_ROW_HEIGHT}
          label="Total seats"
          descriptor={isFiltered ? "installed now" : "installed now - in view"}
          tooltip={<KpiPopover period="Today" source="Envoy + admin">
            Desks installed across these offices - the denominator for seat utilization.
          </KpiPopover>}
          tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
          tooltipPlacement="viewport-clamped"
          value={loading ? "…" : stats.totalCapacity > 0 ? fmtInt(stats.totalCapacity) : "—"}
          rawValue={loading ? undefined : stats.totalCapacity}
          formatter={(n) => (n > 0 ? fmtInt(n) : "—")}
          animationKey={locAnimKey}
          tileIndex={2}
          color="var(--v2-gold)"
          scope="responsive"
          isFiltered={isFiltered}
        />
        <KpiTile
          testId="v2-tile-headcount"
          height={KPI_TILE_ROW_HEIGHT}
          label="Assigned HC"
          descriptor={<span>assigned to offices {!loading && <PartialMarker coverage={assignmentCoverage} measure="assignment" />}</span>}
          value={loading ? headcountStr : assignmentCoverage.known ? headcountTotal.toLocaleString() : "—"}
          rawValue={loading || !assignmentCoverage.known ? undefined : headcountTotal}
          formatter={(n) => (n >= 0 ? fmtInt(n) : "—")}
          animationKey={locAnimKey}
          tileIndex={3}
          color="var(--v2-teal)"
          scope="responsive"
          isFiltered={isFiltered}
          tooltip={<KpiPopover period={formatReportingMonth(headcountReportingMonth) ?? "Workday reporting month unavailable"} source="Workday">
            People assigned to these offices (employees + contractors).
            {" "}{coverageText(assignmentCoverage, "assignment")}
          </KpiPopover>}
          tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
          tooltipPlacement="viewport-clamped"
        />
        {/* Portfolio tiles: always reflect the full usable set (Operational +
            Dormant), never react to the active filter/search. */}
        <KpiTile
          testId="v2-tile-assign-pct"
          height={KPI_TILE_ROW_HEIGHT}
          label="Assign ratio"
          tooltip={<KpiPopover period="Today" source="Ripple portfolio (derived)">
            Assigned headcount vs usable seats - how full the offices are on paper.
          </KpiPopover>}
          tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
          tooltipPlacement="viewport-clamped"
          descriptor="assigned vs installed seats"
          value={loading ? "…" : portfolioAssignPct != null ? `${portfolioAssignPct}%` : "—"}
          rawValue={loading ? undefined : portfolioAssignPct ?? undefined}
          formatter={(n) => `${n}%`}
          animationKey={locAnimKey}
          tileIndex={4}
          color={portfolioAssignPct != null && portfolioAssignPct > 100 ? T.ragRed : portfolioAssignPct != null && portfolioAssignPct >= 90 ? T.ragAmber : "var(--v2-green)"}
          scope="portfolio"
        />
        <KpiTile
          testId="v2-tile-unassigned"
          height={KPI_TILE_ROW_HEIGHT}
          label="Unassigned HC"
          tooltip={<KpiPopover period={formatReportingMonth(headcountReportingMonth) ?? "Workday reporting month unavailable"} source="Workday">
            Headcount with no office assigned (distributed/remote).
          </KpiPopover>}
          tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
          tooltipPlacement="viewport-clamped"
          descriptor="headcount with no office"
          value={reportsLoading ? "…" : unassignedHeadcount == null ? "—" : fmtInt(unassignedHeadcount)}
          rawValue={unassignedHeadcount == null ? undefined : unassignedHeadcount}
          formatter={(n) => (n > 0 ? fmtInt(n) : "—")}
          animationKey={locAnimKey}
          tileIndex={5}
          color="var(--v2-purple)"
          scope="portfolio"
        />
      </KpiBand>

      <>{/* Toolbar */}
      <InlineFilterToolbar
        collectionActions
        testId="v2-toolbar" leftTestId="v2-toolbar-left"
        countBelow={viewMode === VIEW_LIST} reuseCountGap
        afterCount={<div data-testid="offices-search-hint-anchor" style={{ marginTop: hiddenSearchHint ? 4 : 0 }}>
          <FilterHiddenSearchHint hint={hiddenSearchHint} entity="office" empty={filtered.length === 0}
            onClear={() => setSelections(revealedSelections)} />
        </div>}
        countTestId="offices-showing-count" count={filtered.length} total={locations.length}
        trailing={<ViewControls
          object="offices"
          view={viewMode}
          densityNodes={{
            compactComfortable: () => (
              <CompactSwitch
                checked={density === DENSITY_COMPACT}
                onChange={() => {
                  const next = density === DENSITY_COMFORTABLE ? DENSITY_COMPACT : DENSITY_COMFORTABLE;
                  setViewState((s) => ({ ...s, density: next }));
                }}
              />
            ),
          }}
          viewToggle={
            <ToolbarViewToggle
              testId="v2-view-toggle"
              value={viewMode}
              onChange={handleViewMode}
              segments={[
                { key: VIEW_LIST, label: "List", testid: `v2-view-${VIEW_LIST}` },
                { key: VIEW_TILES, label: "Tiles", testid: `v2-view-${VIEW_TILES}` },
                { key: VIEW_MAP, label: "Map", testid: `v2-view-${VIEW_MAP}` },
              ]}
            />
          }
          addButton={canAddLocation ? (
            <ToolbarAddButton variant="primary" testId="v2-add-location-btn" aria-label="+ Add office" onClick={onAddLocation}>
              Add office
            </ToolbarAddButton>
          ) : null}
        />}
      >
        {/* Left zone — Search → Filter → Sort (+ Clear all). Never a
            density control (VIEWCONTROLS-STANDARDIZE-S1). */}
          {/* Alert chip — leads the toolbar row; always visible, scoped per view */}
          <AlertChip
            testId="offices-alert-chip"
            storageKey={`ripple.offices.alertChip.${viewMode}.open`}
            okMessage={{ text: viewMode === VIEW_MAP ? "All offices mapped" : "All office data complete" }}
            messages={officesAlertMessages}
          />

          {/* Search */}
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder="Search offices…"
            testId="v2-search"
          />

          {/* Consolidated filter */}
          <ConsolidatedFilter
            checkedInclusion
            unifiedDefaults
            dimensions={countedFilterDimensions}
            selections={selections}
            onChange={setSelections}
            testId="v2-filter-btn"
            baselineSelections={DEFAULT_SCOPE_SELECTIONS}
          />

          <InlineFilterChips prefix="offices" chips={facetChips({
            dimensions: FILTER_DIMENSIONS, selections, baseline: DEFAULT_SCOPE_SELECTIONS,
            checked: true, onChange: setSelections,
          })} />

          {/* Sort */}
          <SortControl
            testId="v2-sort"
            fields={OFFICES_SORT_FIELDS}
            primary={{ key: sortKey, dir: sortDir }}
            secondary={{ key: sortKey2, dir: sortDir2 }}
            defaultKey="sqft"
            onChange={handleSortChange}
          />

          {/* List Clear preserves search/layout; other lenses retain full reset. */}
          {showToolbarClear && (
            <InlineFilterClear testId="v2-clear-btn" onClick={viewMode === VIEW_LIST ? handleListToolbarClear : handleClear} />
          )}
      </InlineFilterToolbar>

      {/* Error */}
      {error && (
        <div style={{
          background: "var(--v2-red-tint)", border: "1px solid var(--v2-red-border)",
          borderRadius: 10, padding: "12px 16px", marginBottom: 14,
          fontSize: T.fsBase, color: "var(--v2-red)",
        }}>
          Failed to load locations. Please refresh.
        </div>
      )}

      {/* Tiles view */}
      {viewMode === VIEW_TILES && (
        <LocationsGrid
          locations={filtered}
          selectedId={routeLocationId}
          onSelect={(id) => openOfficeDrawer(id)}
          loading={loading}
          error={error}
        />
      )}

      {/* Map view */}
      {viewMode === VIEW_MAP && (
        <LocationsMap
          locations={filtered}
          selectedId={routeLocationId}
          onSelectLocation={(id) => openOfficeDrawer(id)}
          loading={loading}
          error={error}
        />
      )}

      {/* List view (grid table) */}
      {viewMode === VIEW_LIST && (
        <div
          data-testid="v2-grid-table"
          className="offices-collection"
          ref={setGeometryElement}
          style={LIST_PRESENTATION.container}
        >
          <style>{`
            .offices-collection [data-list-row] > div {
              min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
            }
            .offices-collection [data-list-row] > div:nth-child(1) { text-align: left; }
          `}</style>
          {/* Zero-height sentinel — exits the viewport when the header pins,
               triggering isStuck via IntersectionObserver. */}
          <div ref={sentinelRef} aria-hidden="true" style={{ height: 0 }} />
          {/* Header — sticky shell has NO overflow so the browser compositor
               doesn't treat it as a scroll-port (which kills sticky in Chrome/Safari).
               The inner clip div carries overflowX:hidden for scroll-sync and holds
               the ref so programmatic scrollLeft writes still work. */}
          <div
            data-testid="office-column-header"
            style={{
              position: "sticky",
              top: "var(--places-sticky-top, 0px)",
              zIndex: 10,
            }}
          >
          <div
            ref={headerScrollRef}
            data-testid="office-header-clip"
            style={{ overflowX: "hidden", ...listHeaderEdges(isStuck) }}
          >
          <div
            role="row"
            data-office-grid-header=""
            style={{
              display: "grid",
              gridTemplateColumns: GRID_COLS,
              minWidth: GRID_MIN_W,
              height: COLLECTION_GEOMETRY.header,
              boxSizing: "border-box",
              alignItems: "stretch",
              background: "var(--v2-tint)",
              // NOT redundant with the card's borderRadius — card has no overflow:hidden,
              // so without these the header background escapes the card's rounded corners.
              ...listHeaderEdges(isStuck),
              // borderBottom is drawn per-column on each button/div (as a non-layout
              // boxShadow) so the active-sort blue indicator can replace it cleanly
              // without a stacked grey line beneath the blue bar.
              // The row-level drop shadow is shown only when stuck (pinned on scroll)
              // so it doesn't stack as a dark haze behind the active column's blue indicator.
            }}
          >
            {COL_HEADERS.map((col) => {
              const textTooltipRef = {
                hub: hubBtnRef, stage: stageBtnRef, mAndA: mAndABtnRef,
                people: peopleBtnRef, headroom: headroomBtnRef,
                sfPerSeat: assignedPctBtnRef,
              }[col.key];
              const isActive = sortKey === col.key;
              const sortChevron = <CollectionSortCaret testId={isActive ? `v2-sort-indicator-${col.key}` : undefined}
                style={{ position: "absolute", right: col.key === "hub" ? 0 : 2, width: col.key === "hub" ? 8 : 11, top: "50%", transform: "translateY(-50%)", marginLeft: 0 }} />;
              const content = col.sortable ? (
                col.tooltip ? (
                  col.key !== "space_fit_verdict" ? (
                    <React.Fragment key={col.key}>
                      <CollectionSortHeader as="button" active={isActive} direction={sortDir}
                        ref={textTooltipRef}
                        type="button"
                        data-testid={`v2-sort-header-${col.key}`}
                        aria-label={col.key === "peak" ? col.label : undefined}
                        aria-describedby={textIntent.open && activeTextTooltip === col.key ? `${col.key}-header-tooltip-panel` : undefined}
                        {...textIntent.triggerProps}
                        onClick={() => { textIntent.dismiss(); handleSort(col.key); }}
                        onMouseEnter={() => {
                          if (textTooltipKey.current !== col.key) textIntent.dismiss();
                          textTooltipKey.current = col.key;
                          textIntent.enter();
                        }}
                        onFocus={(event) => { textTooltipKey.current = col.key; textIntent.triggerProps.onFocus(event); }}
                        style={{
                          position: "relative",
                          width: "100%",
                          padding: col.key === "hub" ? "10px 8px" : col.align === "right" ? "10px 14px 10px 6px" : "10px 10px",
                          fontSize: T.fsXxs,
                          fontWeight: T.fwBold,
                          lineHeight: "normal",
                          letterSpacing: ".07em",
                          textTransform: "uppercase",
                          textAlign: col.align,
                          whiteSpace: "nowrap",
                          userSelect: "none",
                          border: "none",
                          cursor: "pointer",
                          display: "block",
                          boxShadow: "0 1px 0 var(--v2-line)",
                          background: "none",
                          color: "var(--v2-mid)",
                          ...(isActive
                            ? { borderBottom: "none" }
                            : { borderBottom: "1px solid var(--v2-line)" }),
                        }}
                      >
                        {col.label}
                        {sortChevron}
                      </CollectionSortHeader>
                      <HeaderTextTooltip
                        show={textIntent.open && activeTextTooltip === col.key}
                        triggerRef={textTooltipRef}
                        column={col.key}
                      />
                    </React.Fragment>
                  ) : (
                    <React.Fragment key={col.key}>
                      <CollectionSortHeader as="button" active={isActive} direction={sortDir}
                        ref={spaceFitBtnRef}
                        type="button"
                        data-testid={`v2-sort-header-${col.key}`}
                        {...spaceFitIntent.triggerProps}
                        aria-describedby={spaceFitShow ? "space-fit-header-tooltip-panel" : undefined}
                        onClick={() => { spaceFitIntent.dismiss(); handleSort(col.key); }}
                        style={{
                          position: "relative",
                          width: "100%",
                          padding: "10px 10px",
                          fontSize: T.fsXxs,
                          fontWeight: T.fwBold,
                          lineHeight: "normal",
                          letterSpacing: ".07em",
                          textTransform: "uppercase",
                          textAlign: "center",
                          whiteSpace: "nowrap",
                          userSelect: "none",
                          border: "none",
                          cursor: "pointer",
                          display: "block",
                          boxShadow: "0 1px 0 var(--v2-line)",
                          background: "none",
                          color: "var(--v2-mid)",
                          ...(isActive
                            ? { borderBottom: "none" }
                            : { borderBottom: "1px solid var(--v2-line)" }),
                        }}
                      >
                        {col.label}
                        {sortChevron}
                      </CollectionSortHeader>
                      <SpaceFitHeaderPopover show={spaceFitShow} triggerRef={spaceFitBtnRef} popupProps={spaceFitIntent.popupProps} bufferPct={fitPolicyState.baseline?.hcBufferPct} />
                    </React.Fragment>
                  )
                ) : (
                <div key={col.key} style={{ position: "relative" }}>
                <CollectionSortHeader as="button" active={isActive} direction={sortDir}
                  type="button"
                  data-testid={`v2-sort-header-${col.key}`}
                  onClick={() => handleSort(col.key)}
                  style={{
                    position: "relative",
                    padding: col.key === "location" ? "10px 14px" : "10px 14px 10px 6px",
                    fontSize: T.fsXxs,
                    fontWeight: T.fwBold,
                    lineHeight: "normal",
                    letterSpacing: ".07em",
                    textTransform: "uppercase",
                    color: "var(--v2-mid)",
                    textAlign: col.align,
                    whiteSpace: "nowrap",
                    userSelect: "none",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    width: "100%",
                    display: "block",
                    boxShadow: "0 1px 0 var(--v2-line)",
                    ...(isActive
                      ? { borderBottom: "none" }
                      : { borderBottom: "1px solid var(--v2-line)" }),
                  }}
                >
                  {col.label}
                  {sortChevron}
                </CollectionSortHeader>
                </div>
                )
              ) : (
                <div
                  key={col.key}
                  data-testid={`v2-col-${col.key}`}
                  style={{
                    padding: "10px 14px",
                    fontSize: T.fsXxs,
                    fontWeight: T.fwBold,
                    lineHeight: "normal",
                    letterSpacing: ".07em",
                    textTransform: "uppercase",
                    color: "var(--v2-mid)",
                    textAlign: col.align,
                    whiteSpace: "nowrap",
                    userSelect: "none",
                    borderBottom: "1px solid var(--v2-line)",
                  }}
                >
                  {col.label}
                </div>
              );
              // Keep a real positioning box for the existing overflow control;
              // the sort header's semantic wrapper itself is display:contents.
              return col.key === "headroom"
                ? <div key={col.key} style={{ position: "relative" }}>{content}</div>
                : content;
            })}
          </div>
          </div>
          </div>

          {/* Body wrapper — horizontal scroll only; header scroll is synced via onScroll */}
          {listOverflow.header && createPortal(<OverflowButton overflow={listOverflow.overflow} right={listOverflow.right} />, listOverflow.header)}
          <div ref={bodyScrollRef} onScroll={handleBodyScroll} style={{ overflowX: "auto" }}>
            {loading ? (
              <div style={{ padding: "32px 14px", textAlign: "center", color: "var(--v2-light)", fontSize: T.fsBase }}>
                Loading locations…
              </div>
            ) : filtered.length === 0 ? (
              <div
                data-testid="v2-empty-state"
                style={{ padding: "32px 14px", textAlign: "center", color: "var(--v2-light)", fontSize: T.fsBase }}
              >
                No locations match your filters.{" "}
                {!hiddenSearchHint && isNonDefaultView && (
                  <button
                    type="button"
                    onClick={handleClear}
                    style={{ border: 0, background: "none", color: "var(--v2-accent)",
                             fontWeight: T.fwBold, cursor: "pointer", fontSize: T.fsBase }}
                  >
                    Clear
                  </button>
                )}
              </div>
            ) : (
              filtered.map((loc) => (
                <GridRow
                  matchEvidence={searchMatches.get(loc.id)?.evidence}
                  key={loc.id}
                  loc={loc}
                  onRowClick={handleRowClick}
                  drawerUrl={officeDrawerUrl(loc.id)}
                  selected={drawerLoc?.id === loc.id}
                  density={density}
                  isReconFlagged={reconFlaggedIds.has(loc.id)}
                  isVacatedHcFlagged={vacatedHcFlaggedIds.has(loc.id)}
                  activeSortKey={sortKey}
                  userHasSetSort={viewState.userHasSetSort}
                />
              ))
            )}
          </div>
        </div>
      )}

      </>

      </PageHeaderStack>

      {/* Task #9085: route-owned office record drawer (680px). */}
      <OfficeRecordDrawer
        userKey={userKey}
        locationId={routeLocationId}
        tab={routeTab}
        currentRole={currentRole}
        planData={planData}
        leaseId={!routerLocation.state?.leaseLocationId || routerLocation.state.leaseLocationId === routeLocationId
          ? routerLocation.state?.leaseId ?? null : null}
        orderedIds={viewMode === VIEW_MAP ? mapFilteredIds : routerLocation.state?.orderedIds ?? filteredIds}
        navigate={(to) => navigate(to)}
        onTabChange={(t) => openOfficeDrawer(routeLocationId, { tab: t, state: routerLocation.state })}
        onNavigate={(id) => openOfficeDrawer(id, { tab: routeTab, replace: true, state: routerLocation.state })}
        onClose={closeOfficeDrawer}
        onOpenFull={(id) => handleOpenDetail(id)}
        onOfficeSaved={() => {
          getLocations().then(setLocations).catch((err) => {
            log.error("OfficesPage: saved office refresh failed", { msg: err?.message });
            setError(err);
          });
        }}
        onOpenLease={(leaseId, loc) => navigate(`/leases/${encodeURIComponent(leaseId)}`, {
          state: recordSwapState({ kind: "office", id: loc.id, label: officeIdentityLine(loc) || loc.id, path: `${routerLocation.pathname}${routerLocation.search}` }),
        })}
        recordReturn={drawerReturn}
        onFollowReturn={(to, state) => navigate(to, { state })}
        onOpenMarket={(marketId, loc) => navigate(buildMarketUrl(marketId), {
          state: recordSwapState({ kind: "office", id: loc.id, label: officeIdentityLine(loc) || loc.id, path: `${routerLocation.pathname}${routerLocation.search}` }),
        })}
        onOpenProject={(projectId, loc) => navigate(buildProjectUrl(projectId), {
          state: recordSwapState({ kind: "office", id: loc.id, label: officeIdentityLine(loc) || loc.id, path: `${routerLocation.pathname}${routerLocation.search}` }),
        })}
      />

    </div>
  );
}
